import React, { useState, useEffect, useRef } from "react";
import "./searchFields.css";
import NoLenderModal from "./components/noLenderModal";
import { InputNumber, Select, Checkbox, Radio, Tooltip, Modal, message, Button, Input } from "antd";
import * as com from "../../../../Common.js";
import * as common from "./../common/common.js";
import LodestarTipsCard from "../../../../Management/estimatedclosingfee/components/LodestarTipsCard";
import ClosingCost from "../../../../Management/estimatedclosingfee/components/ClosingCost";
import Lenders from "../../../../Management/Lenders";
import { checkAccess } from "Auth";
import { UR_Owner } from "../../../../State.js";
import {
  BORROWER_DEFAULTS,
  CREDIT_SCORE_MAX,
  CREDIT_SCORE_MIN,
  FIRST_LIEN_PURCHASE_DEFAULTS,
  LOAN_PROGRAM_OPTIONS_BY_LIEN,
  LOAN_PURPOSE_OPTIONS,
  PROGRAM_FIELD_DEFAULTS,
} from "../../config";
import {
  parseSearchFieldsFromUrlQuery,
  matchCountyInLoanLimits,
} from "./queryParams";
import {
  calculateFirstLienAmounts,
  calculateLoanMetrics,
  getCollateralValue,
  getEffectiveAppraisedValue,
  resolveLoanMetricOverrides,
} from "../../loanMetrics";
import AdvancedLoanFields from "./advancedLoanFields";
import { validatePpeSearch } from "./validation";
// PPE: ARM terms are fixed period + adjustment cadence (3/1 ... 10/6); older
// fixed-period-only saved searches restore as the N/6 product.
import {
  ARM_TERM_OPTIONS,
  ARM_TERM_YEARS,
  DEFAULT_ARM_TERM,
  restoreArmTerm,
} from "../../armTerms";
import {
  describeLocation,
  locationForZip,
  lookupZipCounties,
  zip5,
  zipCountiesInState,
  zipMatchesLocation,
} from "../../zipCounty";

const FIXED_TERM_OPTIONS = [
  { value: "fixed40", label: "Fixed 40 Year" },
  { value: "fixed30", label: "Fixed 30 Year" },
  { value: "fixed25", label: "Fixed 25 Year" },
  { value: "fixed20", label: "Fixed 20 Year" },
  { value: "fixed15", label: "Fixed 15 Year" },
  { value: "fixed10", label: "Fixed 10 Year" },
];
const HELOC_TERM_OPTIONS = [
  { value: "fixed30", label: "Fixed 30 Year" },
  { value: "fixed25", label: "Fixed 25 Year" },
  { value: "fixed15", label: "Fixed 15 Year" },
];

// PPE: heloc/secondMortgage are second-lien-only products. Lien position filters
// the available programs, while selecting either second-lien program also moves
// the form to the matching lien position.
const SECOND_LIEN_PROGRAMS = ["heloc", "secondMortgage"];
const isSecondLienProgram = (p) => SECOND_LIEN_PROGRAMS.includes(p);
const INCOME_DOCUMENTATION_PROGRAMS = ["nonqm", "heloc", "secondMortgage"];
const INCOME_DOCUMENTATION_OPTIONS = [
  { value: "FullDoc", label: "Full Doc" },
  { value: "FullDoc12Mo", label: "Full Doc 12mo" },
  { value: "FullDocWithAssets", label: "Full Doc with Assets" },
  { value: "PersonalBankStatement12Mo", label: "Personal Bank Stmt 12mo" },
  { value: "PersonalBankStatement24Mo", label: "Personal Bank Stmt 24mo" },
  { value: "BusinessBankStatement12Mo", label: "Business Bank Stmt 12mo" },
  { value: "BusinessBankStatement24Mo", label: "Business Bank Stmt 24mo" },
  { value: "BankStatement3Mo", label: "Bank Stmt 3mo" },
  { value: "BusinessBankStatement6Mo", label: "Business Bank Stmt 6mo" },
  { value: "Form109912Mo", label: "1099 12mo" },
  { value: "Form109924Mo", label: "1099 24mo" },
  // "P&L" without a preparer is treated as borrower prepared; a P&L prepared
  // by a CPA is its own type (CPAPL). Lenders that do not tell them apart
  // price CPAPL as a 12-month P&L.
  { value: "CPAPL", label: "CPA P&L" },
  { value: "PL", label: "P&L (borrower prepared)" },
  { value: "PL12", label: "P&L 12mo (borrower prepared)" },
  { value: "PL24", label: "P&L 24mo (borrower prepared)" },
  { value: "AssetDepletion", label: "Asset Depletion" },
  { value: "AssetUtilization", label: "Asset Utilization" },
  { value: "AssetQualifier", label: "Asset Qualifier" },
  { value: "AssetBasedIncome", label: "Asset Based Income" },
  { value: "VOE", label: "WVOE" },
  { value: "VOE24Mo", label: "2-year VOE" },
  { value: "DebtServiceCoverageRatio", label: "DSCR" },
];
// PPE: a condominium that does not meet agency warrantability. Sent as the
// attached-condo propertyType plus nonWarrantableCondo: true.
const NON_WARRANTABLE_CONDO_PROPERTY_TYPE = "nonwarrantablecondo";
// PPE: the only multi-unit property type; drives the Units default.
const MULTIFAMILY_PROPERTY_TYPE = "twotofourfamily";
const SEASONING_BUCKETS = new Set([
  "lt12",
  "12-23",
  "24-35",
  "36-47",
  "48-59",
  "60-83",
  "gte84",
]);
const DEFAULT_SEASONING_BUCKET = "lt12";
const BANKRUPTCY_OUTCOMES = new Set(["discharged", "dismissed", "openPending"]);
const DEFAULT_BANKRUPTCY_OUTCOME = "discharged";

function normalizeSeasoningBucket(value) {
  return SEASONING_BUCKETS.has(value) ? value : "";
}

function normalizeBankruptcyOutcome(value) {
  return BANKRUPTCY_OUTCOMES.has(value) ? value : "";
}

function hasMortgageLateCounts(mortgageLates) {
  if (!mortgageLates) return false;
  const last12mo = mortgageLates.last12mo || {};
  const m13to24 = mortgageLates.m13to24 || {};
  return [last12mo, m13to24].some((bucket) =>
    [bucket.x30, bucket.x60, bucket.x90, bucket.x120].some((count) => Number(count) > 0)
  );
}

/** 深链模式：仅预填 URL 中出现的字段，其余留空；普通进入：沿用表单原默认值 */
function pickInitial(deepLink, field, legacyDefault, emptyValue = null) {
  if (!deepLink?.fromDeepLink) return legacyDefault;
  return Object.prototype.hasOwnProperty.call(deepLink, field)
    ? deepLink[field]
    : emptyValue;
}

function calculateDti(monthlyIncome, monthlyDebt) {
  if (
    monthlyIncome === null || monthlyIncome === undefined || monthlyIncome === "" ||
    monthlyDebt === null || monthlyDebt === undefined || monthlyDebt === ""
  ) {
    return null;
  }
  const income = Number(monthlyIncome);
  const debt = Number(monthlyDebt);
  if (!Number.isFinite(income) || income <= 0 || !Number.isFinite(debt) || debt < 0) {
    return null;
  }
  return Number(((debt / income) * 100).toFixed(2));
}

const SearchFields = ({ onCheckRates, historyParams , onBackToResult}) => {
  const [deepLinkInitial] = useState(() => parseSearchFieldsFromUrlQuery());
  const fromDeepLink = deepLinkInitial?.fromDeepLink === true;
  const queryCountySyncedRef = useRef(false);
  const [showEditCostDetails, setShowEditCostDetails] = useState(false);
  const [lodeStarConfig, setLodeStarConfig] = useState({
    suggestEnableCount: 1,
    isModified: false,
    enableOriginationCharges: false,
    enableCanNotShopServices: false,
    enableCanShopServices: false,
    enableTaxesAndOtherGovernmentFees: false,
    enablePrepaids: false,
    enableInitialEscrowPaymentsAtClosing: false,
    enableOther: false,
  });
  const [closingFeeConfig, setClosingFeeConfig] = useState({
    originationCharges: null,
    canNotShopServices: null,
    canShopServices: null,
    taxesAndOtherGovernmentFees: null,
    prepaids: null,
    initialEscrowPaymentsAtClosing: null,
    other: null,
  });
  const [totalClosingFee, setTotalClosingFee] = useState(0);
  const closingCostRef = useRef();

  const [isOpen_no_lender, setIsOpen_no_lender] = useState(false);
  const [isCalculatedAtResults, setIsCalculatedAtResults] = useState(false);
  const [hasLenders, setHasLenders] = useState(false);
  const [shouldCheckRatesAfterLenderUpdate, setShouldCheckRatesAfterLenderUpdate] = useState(false);
  // const [loading, setLoading] = useState(false);
  // const [connectedLendersMap, setConnectedLendersMap] = useState({});
  // const [resultlenderSet, setResultlenderSet] = useState(new Set());
  // const [lenderData, setLenderData] = useState([]);
  // const [selectedLenders, setSelectedLenders] = useState([]);
  // const [showConnectedLendersOnly, setShowConnectedLendersOnly] =
  //   useState(false);
  // const [showIneligible, setShowIneligible] = useState(false);
  // const [showAddLendersModal, setShowAddLendersModal] = useState(false);
  // const [ineligibleProducts, setIneligibleProducts] = useState({});
  // const [noProducts, setNoProducts] = useState(false);
  // const [productTypes, setProductTypes] = useState([]);
  // const [loans, setLoans] = useState([]);
  // const [indexFixed30, setIndexFixed30] = useState(0);
  // const [indexFixed25, setIndexFixed25] = useState(0);
  // const [indexFixed20, setIndexFixed20] = useState(0);
  // const [indexFixed15, setIndexFixed15] = useState(0);
  // const [indexFixed10, setIndexFixed10] = useState(0);
  // const [indexArm56, setIndexArm56] = useState(0);
  // const [indexArm76, setIndexArm76] = useState(0);
  // const [indexArm106, setIndexArm106] = useState(0);
  // const [selectedAUS, setSelectedAUS] = useState([]);
  // const [allRates, setAllRates] = useState({});
  const [searchBy, setSearchBy] = useState("price");
  const [targetRate, setTargetRate] = useState(null);
  const [targetRateMessage, setTargetRateMessage] = useState('');
  const [from, setFrom] = useState("search");
  const [isLoading, setIsLoading] = useState(false);
  // product
  const [targetPrice, setTargetPrice] = useState(100);
  const [targetPriceType, setTargetPriceType] = useState([""]); // calcOptions default select all
  const [lockDay, setLockDay] = useState(30);
  const [compensationType, setCompensationType] = useState("lender_paid");
  const [compensationPercent, setCompensationPercent] = useState(0);
  const [estimatedClosingFee, setEstimatedClosingFee] = useState(0);
  // keep this an array, never undefined: antd/rc-util treats a `undefined`
  // value as "uncontrolled" and falls back to the Select's own internal state,
  // so the tags would survive a reset while ausType no longer has a .length.
  const [ausType, setAusType] = useState([]);
  const [buydown, setBuydown] = useState("none");

  // product

  // product
  const svg_expand = () => {
    return <svg xmlns="http://www.w3.org/2000/svg" width="17" height="20" viewBox="0 0 17 20" fill="none">
    <path fill-rule="evenodd" clip-rule="evenodd" d="M8.80039 5.15746L7.9165 6.04134L11.7462 9.871L7.91804 13.6991L8.80192 14.583L12.63 10.7549L12.6305 10.7554L13.5144 9.8715L13.5139 9.871L13.516 9.86896L12.6321 8.98508L12.63 8.98712L8.80039 5.15746Z" fill="#325CEB"/>
  </svg>
  }
  const updateTargetPrice = (value) => {
    if(value === null){
      setTargetRateMessage('Please enter target price');
      // return;
    }else if(value < 80 || value > 120){
      setTargetRateMessage('Target price must be between 80 and 120');
      // return;
    }else{
      setTargetRateMessage('');
    }
    setTargetPrice(value);
  };

  const updateTargetPriceType = (value) => {
    if (value.length > 2) {
      setTargetPriceType([""]);
    } else if (value.length > 1 && value.includes("") && value.length < 3) {
      setTargetPriceType(value.filter((item) => item !== ""));
    } else {
      setTargetPriceType(value);
    }
  };

  const updateLockDay = (value) => {
    // antd Select returns the option value as a string ("60"); keep lockDay
    // numeric so the strict lockDay comparison in the result table matches.
    setLockDay(Number(value) || 30);
  };

  const updateCompensationType = (value) => {
    setCompensationType(value);
  };

  const updateCompensationPercent = (value) => {
    setCompensationPercent(value);
  };

  const updateEstimatedClosingFee = (value) => {
    setEstimatedClosingFee(value);
  };

  const updateAusType = (value) => {
    setAusType(value);
  };


  const updateBuydown = (value) => {
    setBuydown(value);
  };

  // product

  // borrower
  const [fico, setFico] = useState(pickInitial(deepLinkInitial, "fico", 780, null));
  const initialMonthlyIncome = pickInitial(deepLinkInitial, "monthlyIncome", 10000, null);
  const initialMonthlyDebt = pickInitial(deepLinkInitial, "monthlyDebt", 1000, null);
  const [monthlyIncome, setMonthlyIncome] = useState(initialMonthlyIncome);
  const [monthlyDebt, setMonthlyDebt] = useState(initialMonthlyDebt);
  const [dti, setDti] = useState(() => calculateDti(initialMonthlyIncome, initialMonthlyDebt));
  const [citizenship, setCitizenship] = useState("US_CITIZENS");
  const [isFirstTimeHomeBuyer, setIsFirstTimeHomeBuyer] = useState(true); // "Yes"
  const [isSelfEmployed, setIsSelfEmployed] = useState(false);
  const [isImpound, setIsImpound] = useState(true); // "Yes"
  const [isInterestOnly, setIsInterestOnly] = useState(false);
  const [isWaiveLenderFee, setIsWaiveLenderFee] = useState(false);
  const [ficoError, setFicoError] = useState("");

  // property
  const [occupancy, setOccupancy] = useState(
    pickInitial(deepLinkInitial, "occupancy", "principal", undefined),
  );
  const [propertyType, setPropertyType] = useState("singlefamilydetached");
  const [state, setState] = useState(
    pickInitial(deepLinkInitial, "state", "CALIFORNIA", undefined),
  );
  const [county, setCounty] = useState(
    pickInitial(deepLinkInitial, "county", "ALAMEDA COUNTY", undefined),
  );
  const [isRural, setIsRural] = useState(false);
  const [isMixedUse, setIsMixedUse] = useState(false);

  // borrower

  const updateFico = (value) => {
    if (value < CREDIT_SCORE_MIN || value > CREDIT_SCORE_MAX) {
      setFicoError(`Enter a FICO score between ${CREDIT_SCORE_MIN} and ${CREDIT_SCORE_MAX}.`);
    } else {
      setFicoError("");
    }
    setFico(Number(value));
  };

  const updateMonthlyIncome = (value) => {
    setMonthlyIncome(value);
    setDti(calculateDti(value, monthlyDebt));
  };

  const updateMonthlyDebt = (value) => {
    setMonthlyDebt(value);
    setDti(calculateDti(monthlyIncome, value));
  };

  const updateCitizenship = (value) => {
    setCitizenship(value);
  };

  const updateFirstTimeHomeBuyer = (e) => {
    setIsFirstTimeHomeBuyer(e.target.checked);
  };

  const updateSelfEmployed = (e) => {
    setIsSelfEmployed(e.target.checked);
  };

  const updateImpound = (e) => {
    setIsImpound(e.target.checked);
  };

  const updateInterestOnly = (e) => {
    setIsInterestOnly(e.target.checked);
  };

  const updateWaiveLenderFee = (e) => {
    setIsWaiveLenderFee(e.target.checked);
  };

  const updateOccupancy = (value) => {
    setOccupancy(value);
  };

  const updatePropertyType = (value) => {
    setPropertyType(value);
    // 2-4 unit multifamily starts at 2 units; every other type is single-unit.
    setPpeUnits(value === MULTIFAMILY_PROPERTY_TYPE ? 2 : 1);
  };

  // State or County picked by hand: a ZIP outside the new county is cleared,
  // so the request never carries a ZIP, state and county that disagree.
  const clearZipOutside = (nextState, nextCounty) => {
    const counties = currentZipCounties();
    if (!zipMatchesLocation(counties, nextState, nextCounty)) {
      setZipNote(`Cleared ZIP ${zip5(ppeZip)}: it is not in ${describeLocation(nextState, nextCounty)}.`);
      setPpeZip("");
    }
  };

  const updateState = (value) => {
    setState(value);
    // 当state变化时，自动选择第一个可用的county
    // (the ZIP's own county in that state when there is one)
    let nextCounty = "";
    if (value && value in loanLimits) {
      const countyKeys = Object.keys(loanLimits[value]);
      nextCounty = zipCountiesInState(currentZipCounties(), value, loanLimits)[0] || countyKeys[0] || "";
    }
    setCounty(nextCounty);
    clearZipOutside(value, nextCounty);
  };

  const updateCounty = (value) => {
    setCounty(value);
    clearZipOutside(state, value);
  };

  const updateRural = (e) => {
    setIsRural(e.target.checked);
  };

  const updateMixedUse = (e) => {
    setIsMixedUse(e.target.checked);
  };

  // borrower

  // loan
  const [loanPurpose, setLoanPurpose] = useState(
    pickInitial(deepLinkInitial, "purpose", "Purchase", undefined),
  );
  const [loanTerm, setLoanTerm] = useState("fixed30");
  const [loanType, setLoanType] = useState(["conventional"]);
  // PPE: explicit merged program selector (drives QM/NonQM routing on the new endpoint)
  const [loanProgram, setLoanProgram] = useState("conventional");
  // Keep the full Non-QM details state independent from the compact second-lien
  // documentation controls so changing one flow cannot alter the other.
  const [nqIncomeDoc, setNqIncomeDoc] = useState("FullDoc");
  const [nqDscrMode, setNqDscrMode] = useState("number");
  const [nqDscr, setNqDscr] = useState(1.2);
  const [secondIncomeDoc, setSecondIncomeDoc] = useState("FullDoc");
  const [secondDscrMode, setSecondDscrMode] = useState("number");
  const [secondDscr, setSecondDscr] = useState(1.2);
  const [nqHousingEvent, setNqHousingEvent] = useState("");
  const [nqHousingSeasoning, setNqHousingSeasoning] = useState("");
  const [nqBkType, setNqBkType] = useState("");
  const [nqBkOutcome, setNqBkOutcome] = useState("");
  const [nqBkSeasoning, setNqBkSeasoning] = useState("");
  const [nqHasMortgageLates, setNqHasMortgageLates] = useState(false);
  const [nqLates, setNqLates] = useState({ l12_30: 0, l12_60: 0, l12_90: 0, l12_120: 0, l24_30: 0, l24_60: 0, l24_90: 0, l24_120: 0 });
  const [nqShortTermRental, setNqShortTermRental] = useState(false);
  const [nqVacant, setNqVacant] = useState(false);
  const [nqFirstTimeInvestor, setNqFirstTimeInvestor] = useState(false);
  const [nqEntityVesting, setNqEntityVesting] = useState(false);
  const [nqRural, setNqRural] = useState(false);
  const [nqUnique, setNqUnique] = useState(false);
  const setLate = (k) => (v) => setNqLates((p) => ({ ...p, [k]: Number(v) || 0 }));
  // PPE: additional fields carried by the unified pricing request.
  const [ppeLien, setPpeLien] = useState("first");
  const [ppeAmort, setPpeAmort] = useState("fixed");
  const [ppeArmTerm, setPpeArmTerm] = useState(5);
  const [ppeAppraised, setPpeAppraised] = useState(null);
  // First-lien appraisal is an Advanced-section override. A collapsed
  // Advanced section must use the visible Home value for pricing.
  const [ppeAppraisalOverrideActive, setPpeAppraisalOverrideActive] = useState(false);
  const [ppeTotalLoan, setPpeTotalLoan] = useState(null);
  const [ppeCashOut, setPpeCashOut] = useState(null);
  const [ppeCashOutError, setPpeCashOutError] = useState("");
  const [ppeCltv, setPpeCltv] = useState(null);
  const [ppeHcltv, setPpeHcltv] = useState(null);
  // The default second-lien scenario is 50k behind a 250k first mortgage on the
  // form's 500k property baseline, producing a 60% CLTV out of the box. The line
  // amount also satisfies the engine's required HELOC field.
  const [ppeLine, setPpeLine] = useState(PROGRAM_FIELD_DEFAULTS.helocLineAmount);
  const [ppeDraw, setPpeDraw] = useState(PROGRAM_FIELD_DEFAULTS.helocDrawAmount);
  const [ppeDrawTermMonths, setPpeDrawTermMonths] = useState(PROGRAM_FIELD_DEFAULTS.helocDrawTermMonths);
  const [ppeHelocAmountError, setPpeHelocAmountError] = useState("");
  const [ppePrimaryMtg, setPpePrimaryMtg] = useState(
    PROGRAM_FIELD_DEFAULTS.secondLienPrimaryMortgageAmount,
  );
  // Percent. The engine rejects loanProgram=va without a fundingFee (unless the
  // exempt flag is set), so this cannot start empty. 2.15 = first use, <5% down.
  const [ppeFundingFee, setPpeFundingFee] = useState(PROGRAM_FIELD_DEFAULTS.vaFundingFee);
  const [ppeVaSubseq, setPpeVaSubseq] = useState(false);
  const [ppeVaExempt, setPpeVaExempt] = useState(false);
  // Percent. Same story as fundingFee: loanProgram=usda is rejected without it.
  const [ppeGuaranteeFee, setPpeGuaranteeFee] = useState(PROGRAM_FIELD_DEFAULTS.usdaGuaranteeFee);
  const [ppeReserves, setPpeReserves] = useState(
    pickInitial(deepLinkInitial, "reserves", BORROWER_DEFAULTS.reserves, BORROWER_DEFAULTS.reserves),
  );
  const [ppePropsFinanced, setPpePropsFinanced] = useState(
    pickInitial(
      deepLinkInitial,
      "propertiesFinanced",
      BORROWER_DEFAULTS.propertiesFinanced,
      BORROWER_DEFAULTS.propertiesFinanced,
    ),
  );
  const [ppeNonOccupant, setPpeNonOccupant] = useState(false);
  const [ppeItin, setPpeItin] = useState(false);
  const [ppeUnits, setPpeUnits] = useState(1);
  const [ppeZip, setPpeZip] = useState("");
  // { zip, counties } for the ZIP in the field (see lookupZipCounties); only
  // read, never applied, so restoring a saved search keeps its state/county.
  const [zipLookup, setZipLookup] = useState(null);
  const [zipNote, setZipNote] = useState("");
  const latestFieldsRef = useRef({});
  const [ppePrepay, setPpePrepay] = useState(0);
  const [ppeInterestOnly, setPpeInterestOnly] = useState(false);
  const [ppeAdvOpen, setPpeAdvOpen] = useState(false);
  const [propertyValue, setPropertyValue] = useState(
    pickInitial(deepLinkInitial, "housePrice", FIRST_LIEN_PURCHASE_DEFAULTS.propertyValue, null),
  );
  const [propteryError, setPropteryError] = useState('');
  const [loanAmount, setLoanAmount] = useState(
    // legacy default: propertyValue 500000 - downPayment 100000 (LTV 80)
    pickInitial(deepLinkInitial, "loanAmount", FIRST_LIEN_PURCHASE_DEFAULTS.loanAmount, null),
  );
  // FHA UFMIP as a PERCENT of the base loan amount (engine default: 1.75).
  // Converted to a dollar amount at request-build time (result/index.js).
  const [upFrontMIP, setUpFrontMIP] = useState(1.75);
  const [upFrontMIP_Financed, setUpFrontMIP_Financed] = useState(true);
  const [downPayment, setDownPayment] = useState(() => {
    if (!deepLinkInitial?.fromDeepLink) return FIRST_LIEN_PURCHASE_DEFAULTS.downPayment;
    const { housePrice, loanAmount } = deepLinkInitial;
    if (housePrice != null && loanAmount != null) {
      return Math.max(housePrice - loanAmount, 0);
    }
    return null;
  });
  const [secondaryLoanAmount, setSecondaryLoanAmount] = useState(
    FIRST_LIEN_PURCHASE_DEFAULTS.secondaryLoanAmount,
  );
  const [isSubordinateLiens, setIsSubordinateLiens] = useState(false);
  const [lienType, setLienType] = useState();
  const [lienPosition, setLienPosition] = useState();
  const [helocLineAmount, setHelocLineAmount] = useState(0);
  const [helocDrawAmount, setHelocDrawAmount] = useState(0);
  const [helocLtv, setHelocLtv] = useState(0);

  const getEffectivePpeAppraisedValue = () => getEffectiveAppraisedValue({
    propertyValue,
    appraisedValue: ppeAppraised,
    useAppraisal: isSecondLienProgram(loanProgram) || ppeAppraisalOverrideActive,
  });

  const getDerivedFirstLienAmounts = () => calculateFirstLienAmounts({
    loanPurpose,
    propertyValue,
    downPayment,
    loanAmount,
    secondaryLoanAmount,
    totalLoanAmount: ppeAdvOpen ? ppeTotalLoan : null,
  });

  // loan

  // Keep lien position in sync with the program so no invalid pair can reach
  // the pricing endpoint.
  const updateLoanProgram = (value) => {
    setLoanProgram(value);
    setPpeLien(isSecondLienProgram(value) ? "second" : "first");
    // Program switches start a fresh loan scenario. Do not carry hidden
    // Advanced overrides or subordinate financing into a different product.
    setPpeTotalLoan(null);
    setPpeCltv(null);
    setPpeHcltv(null);
    setSecondaryLoanAmount(0);
    if (!isSecondLienProgram(value)) {
      // The first-lien appraisal is an Advanced override. Keep the value for
      // possible later editing, but do not let a second-lien appraisal leak
      // into a collapsed first-lien search.
      setPpeAppraisalOverrideActive(false);
    }
    if (isSecondLienProgram(value)) {
      setSecondIncomeDoc("FullDoc");
      setSecondDscrMode("number");
    }
    if (isSecondLienProgram(loanProgram) && !isSecondLienProgram(value)) {
      // Leaving a second-lien product must not carry its subject amount into a
      // first-lien search. Restore the purpose-appropriate first-lien baseline.
      if (loanPurpose === "Purchase") {
        setPropertyValue(FIRST_LIEN_PURCHASE_DEFAULTS.propertyValue);
        setDownPayment(FIRST_LIEN_PURCHASE_DEFAULTS.downPayment);
        setLoanAmount(FIRST_LIEN_PURCHASE_DEFAULTS.loanAmount);
      } else {
        setPropertyValue(500000);
        setDownPayment(null);
        setLoanAmount(100000);
      }
    }
    if (value === "heloc") {
      // Keep the HELOC defaults aligned with the existing production PPE form.
      // The controls remain editable and updateAmortization keeps term options
      // synchronized when the user explicitly switches to ARM.
      setPpeAmort("fixed");
      setLoanTerm("fixed30");
      setPpeLine(PROGRAM_FIELD_DEFAULTS.helocLineAmount);
      setPpeDraw(PROGRAM_FIELD_DEFAULTS.helocDrawAmount);
      setPpeDrawTermMonths(PROGRAM_FIELD_DEFAULTS.helocDrawTermMonths);
      setPpePrimaryMtg(PROGRAM_FIELD_DEFAULTS.secondLienPrimaryMortgageAmount);
      setPpeHelocAmountError("");
    } else if (value === "secondMortgage") {
      if (loanProgram === "heloc") {
        setPpeAmort("fixed");
        setLoanTerm("fixed30");
      }
      setLoanAmount(PROGRAM_FIELD_DEFAULTS.secondMortgageAmount);
      setPpePrimaryMtg(PROGRAM_FIELD_DEFAULTS.secondLienPrimaryMortgageAmount);
    } else if (loanProgram === "heloc") {
      setPpeAmort("fixed");
      setLoanTerm("fixed30");
    }
  };

  const updatePpeLien = (value) => {
    const nextProgram = value === "second"
      ? (isSecondLienProgram(loanProgram) ? loanProgram : "secondMortgage")
      : (isSecondLienProgram(loanProgram) ? "conventional" : loanProgram);
    setPpeLien(value);
    if (nextProgram !== loanProgram) {
      updateLoanProgram(nextProgram);
    }
  };

  const updateLoanPurpose = (value) => {
    setLoanPurpose(value);
    if(value === "Purchase"){
      setPropertyValue(FIRST_LIEN_PURCHASE_DEFAULTS.propertyValue);
      setDownPayment(FIRST_LIEN_PURCHASE_DEFAULTS.downPayment);
      setLoanAmount(
        loanProgram === "secondMortgage"
          ? PROGRAM_FIELD_DEFAULTS.secondMortgageAmount
          : FIRST_LIEN_PURCHASE_DEFAULTS.loanAmount,
      );
    } else {
      setPropertyValue(500000);
      setLoanAmount(
        loanProgram === "secondMortgage"
          ? PROGRAM_FIELD_DEFAULTS.secondMortgageAmount
          : 100000,
      );
      setDownPayment(null);
    }
    // A purpose change starts a new collateral-value scenario. Do not carry a
    // hidden appraisal override from the previous purpose.
    setPpeAppraised(null);
    setPpeAppraisalOverrideActive(false);
    setPpeCashOut(null);
    setPpeCashOutError("");
    setPpeTotalLoan(null);
    setPpeCltv(null);
    setPpeHcltv(null);
    setSecondaryLoanAmount(0);
    setPropteryError('');
  };

  const updatePpeAppraised = (value) => {
    setPpeAppraised(value);
    setPpeAppraisalOverrideActive(value !== undefined && value !== null && value !== "");
  };

  const updatePpeCashOut = (value) => {
    setPpeCashOut(value);
    if (loanPurpose === "CashOutRefi" && !(Number(value) > 0)) {
      setPpeCashOutError("Cash-Out amount must be greater than $0.");
    } else if (Number(value) > Number(loanProgram === "heloc" ? ppeDraw : loanAmount || 0)) {
      setPpeCashOutError("Cash-Out amount cannot exceed the new loan amount.");
    } else {
      setPpeCashOutError("");
    }
  };

  const toggleAdvanced = () => {
    // Opening Advanced only reveals optional overrides. It must not activate a
    // value restored from an older search; activation happens when the user
    // actually edits Appraised value. Closing Advanced disables the override
    // so hidden data can never affect pricing.
    setPpeAdvOpen((open) => {
      if (open) {
        setPpeAppraisalOverrideActive(false);
        setPpeTotalLoan(null);
        setPpeCltv(null);
        setPpeHcltv(null);
        setSecondaryLoanAmount(0);
      }
      return !open;
    });
  };

  const updateVaExemptFundingFee = (event) => {
    const exempt = event.target.checked;
    setPpeVaExempt(exempt);
    setPpeFundingFee(exempt ? 0 : PROGRAM_FIELD_DEFAULTS.vaFundingFee);
  };

  const updateLoanTerm = (value) => {
    setLoanTerm(value);
    if (ARM_TERM_YEARS[value]) {
      setPpeArmTerm(ARM_TERM_YEARS[value]);
    }
  };

  const updateAmortization = (value) => {
    setPpeAmort(value);
    if (value === "arm") {
      if (!String(loanTerm).startsWith("arm")) {
        setLoanTerm(DEFAULT_ARM_TERM);
        setPpeArmTerm(ARM_TERM_YEARS[DEFAULT_ARM_TERM]);
      }
    } else if (value === "fixed" && !String(loanTerm).startsWith("fixed")) {
      setLoanTerm("fixed30");
    }
  };

  const loanTypeSelect = (value) => {
    // 如果选择的是 ALL
    if (value === "") {
      setLoanType([""]);
      return;
    }
    
    // 如果当前已经选择了 ALL，则移除 ALL
    if (loanType.includes("")) {
      setLoanType([value]);
      return;
    }

    // 如果选择了所有具体选项（除了ALL），自动添加ALL
    if (loanType.length === 4 && !loanType.includes(value)) {
      setLoanType([""]);
      return;
    }

    // 其他情况正常添加
    setLoanType([...loanType, value]);
  };

  const loanTypeDeselect = (value) => {
    // 如果取消选择的是 ALL
    if (value === "") {
      setLoanType([]);
      return;
    }

    // 如果取消选择后只剩下一个选项，则添加 ALL
    if (loanType.length === 1 && loanType.includes(value)) {
      setLoanType([""]);
      return;
    }

    // 其他情况正常移除
    setLoanType(loanType.filter(item => item !== value));
  };

  const updatePropertyValue = (value) => {
    setPropertyValue(value);
    if (loanPurpose === "Purchase" && !isSecondLienProgram(loanProgram)) {
      setLoanAmount(value - downPayment);
    }
    check_property_value('propertyValue', value);
  };

  const updateLoanAmount = (value) => {
    setLoanAmount(value);
    if (loanPurpose === "Purchase" && !isSecondLienProgram(loanProgram)) {
      setDownPayment(propertyValue - value);
    }
    check_property_value('loanAmount', value);
  };

  const updateDownPayment = (value) => {
    setDownPayment(value);
    setLoanAmount(propertyValue - value);
    check_property_value('downPayment', value);
  };

  const updateSecondaryLoanAmount = (value) => {
    setSecondaryLoanAmount(value);
  };

  const updateLtv = (value) => {
    const collateralValue = getCollateralValue({
      loanPurpose,
      propertyValue,
      appraisedValue: getEffectivePpeAppraisedValue(),
    });
    const nextLoanAmount = collateralValue * (Number(value) || 0) / 100;
    if (loanProgram === "heloc") {
      setPpeDraw(nextLoanAmount);
      validatePpeHelocAmounts(ppeLine, nextLoanAmount);
      return;
    }
    if (loanPurpose === "Purchase" && !isSecondLienProgram(loanProgram)) {
      setDownPayment(Number(propertyValue) - Number(nextLoanAmount.toFixed(0)));
      setLoanAmount(nextLoanAmount.toFixed(0));
      check_property_value('downPayment', Number(propertyValue) - nextLoanAmount);
    }else{
      setLoanAmount(nextLoanAmount);
      check_property_value('loanAmount', nextLoanAmount);
    }
  };

  const updateSubordinateLiens = (e) => {
    setIsSubordinateLiens(e.target.checked);
  };

  const updateLienType = (value) => {
    setLienType(value);
    if (value === "Closed_end") {
      setLienPosition("2nd");
    } else {
      setLienPosition("");
    }
  };

  const updateLienPosition = (value) => {
    setLienPosition(value);
  };

  const updateHelocLineAmount = (value) => {
    setHelocLineAmount(value);
  };

  const updateHelocDrawAmount = (value) => {
    setHelocDrawAmount(value);
  };

  const updateHelocLtv = (value) => {
    setHelocLtv(value);
  };

  // A HELOC draw is a utilization of the approved line, never a second
  // independent loan amount. Keep this constraint visible in the form
  // so an invalid request is stopped before the backend returns a 400.
  const validatePpeHelocAmounts = (line, draw) => {
    const lineAmount = Number(line);
    const drawAmount = Number(draw);
    if (Number.isFinite(lineAmount) && Number.isFinite(drawAmount) && drawAmount > lineAmount) {
      setPpeHelocAmountError("Draw amount cannot exceed the line amount.");
      return false;
    }
    setPpeHelocAmountError("");
    return true;
  };

  // property

  const [loanLimits, setLoanLimits] = useState({});

  const getloanestimatedclosingfee = () => {
    let token = sessionStorage.getItem("ZeitroA");
    fetch("/los/getloanestimatedclosingfee", {
      method: "GET",
      headers: {
        Authorization: "Bearer " + token,
        Cache: "no-cache",
      },
    })
      .then((response) => {
        if (response.status !== 200) {
          alert(
            "Looks like there was a problem. Status Code: " + response.status
          );
          return;
        }
        response.json().then((js) => {
          if (js.status !== "OK") {
          } else {
            let data = js.configurations;
            if (data.estimatedClosingCostsDetail) {
              setClosingFeeConfig(data.estimatedClosingCostsDetail);
            }
            if (data.lodeStarConfig) {
              setLodeStarConfig(data.lodeStarConfig);
            }
          }
        });
      })
      .catch(function (err) {});
  };

  const getLoanLimits = () => {
    fetch("/data/loanlimits")
      .then((res) => {
        if (!res.ok) {
          throw Error(res.statusText);
        }
        return res.json();
      })
      .then((res) => {
        setLoanLimits(res);
      })
      .catch((err) => {
        console.error(err);
      });
  };

  const getPropertyValue = (item) => {
    return (item.loanAmount / item.LTV * 100)
  }

  // useEffect(() => {
  //   setMonthlyDebt(parseInt(monthlyIncome * dti / 100));
  // }, [monthlyIncome, dti]);

  useEffect(() => {
    getLender();
    getloanestimatedclosingfee();
    getLoanLimits();
  }, []);

  useEffect(() => {
    const zip = zip5(ppeZip);
    if (!zip) {
      setZipLookup(null);
      return undefined;
    }
    let cancelled = false;
    lookupZipCounties(zip)
      .then((counties) => {
        if (!cancelled) setZipLookup({ zip, counties });
      })
      .catch((err) => {
        console.error(err);
        if (!cancelled) setZipLookup(null);
      });
    return () => {
      cancelled = true;
    };
  }, [ppeZip]);

  useEffect(() => {
    if (
      !fromDeepLink ||
      queryCountySyncedRef.current ||
      !deepLinkInitial?.state ||
      !deepLinkInitial?.county ||
      !loanLimits[deepLinkInitial.state]
    ) {
      return;
    }

    const matchedCounty = matchCountyInLoanLimits(
      deepLinkInitial.county,
      deepLinkInitial.state,
      loanLimits,
    );
    queryCountySyncedRef.current = true;
    if (matchedCounty) {
      setCounty(matchedCounty);
    }
  }, [loanLimits, fromDeepLink, deepLinkInitial]);

  useEffect(() => {
    // console.log("apply params to current search Fields", historyParams);

    if (historyParams === null) {
      return;
    }
    setFrom(historyParams?.from || "search");
    // apply params to current search Fields
    // loan
    const restoredPurpose = historyParams?.purpose || "";
    setLoanPurpose(restoredPurpose);
    setLoanTerm(restoreArmTerm(historyParams?.loanTerm) || "");
    setLoanType(historyParams?.loanType || historyParams?.loanTypes || [""]);
    setPropertyValue(historyParams?.propertyValue || getPropertyValue(historyParams)); // 需要计算 
    setLoanAmount(historyParams?.loanAmount || 0);
    setUpFrontMIP(Number(historyParams?.upFrontMIP) || 1.75); // percent
    setUpFrontMIP_Financed(historyParams?.upFrontMIP_Financed);
    setDownPayment(
      restoredPurpose === "Purchase"
        ? (historyParams?.downPayment ?? (
            Number(historyParams?.propertyValue || 0) - Number(historyParams?.loanAmount || 0)
          ))
        : null,
    );
    setCltv(historyParams?.CLTV ? Number(Number(historyParams.CLTV).toFixed(2)) : 0);
    setHcltv(historyParams?.HCLTV ? Number(Number(historyParams.HCLTV).toFixed(2)) : 0);
    setIsSubordinateLiens(historyParams?.isSubordinateLiens || false);
    setSecondaryLoanAmount(historyParams?.secondaryLoanAmount || 0);

    // property
    setOccupancy(common.transFormOccupancy(historyParams?.occupancy) || "");
    setPropertyType(
      // A non-warrantable condo is saved as the attached-condo enum plus
      // nonWarrantableCondo.
      historyParams?.nonWarrantableCondo
        ? NON_WARRANTABLE_CONDO_PROPERTY_TYPE
        : (common.transFormPropertyType(historyParams?.property) || "")
    );
    setState(historyParams?.state || "");
    setCounty(historyParams?.county || "");
    setIsRural(historyParams?.isRural || false);
    setIsMixedUse(historyParams?.isMixedUseProperty || false); // isMixedUseProperty // isMixedUse

    // borrower
    setFico(historyParams?.FICO || 0);
    const restoredMonthlyIncome = historyParams?.monthlyIncome ?? 0;
    const restoredMonthlyDebt = historyParams?.monthlyDebt ?? 0;
    setMonthlyIncome(restoredMonthlyIncome);
    setMonthlyDebt(restoredMonthlyDebt);
    setDti(calculateDti(restoredMonthlyIncome, restoredMonthlyDebt));
    setCitizenship(historyParams?.citizenship || "US_CITIZENS");
    setIsFirstTimeHomeBuyer(historyParams?.firsttimehomebuyer || historyParams?.firstTimeHomeBuyer || false); // firsttimehomebuyer // firstTimeHomeBuyer
    setIsSelfEmployed(historyParams?.selfEmployed || historyParams?.selfemployed || false); // selfemployed // selfEmployed

    // product
    setSearchBy(historyParams?.searchBy || "price");
    setTargetPrice(historyParams.searchBy === "price" ? historyParams.targetPrice : 100  || 7);
    setTargetRate(historyParams.searchBy === "rate" ? historyParams?.targetRate || null : historyParams.targetPrice || 100); // 只记录了一个字段 用searchBy区分
    setTargetPriceType(historyParams?.targetPriceType || [""]);
    setLockDay(Number(historyParams?.lockDay) || 30);
    setCompensationType(historyParams?.compensationType || "");
    setCompensationPercent(historyParams?.compensationPercent || 0); // compensationPercentage // compensationPercent
    setEstimatedClosingFee(historyParams?.estimatedClosingCosts || 0); // estimatedClosingCosts // estimatedClosingFee
    setAusType(historyParams?.ausTypes || []);
    setBuydown(historyParams?.buydown ?? "none");
    setIsImpound(historyParams?.isImpound || false); // Impound  // escrowWaiver // || historyParams?.escrowWaiver
    setIsInterestOnly(historyParams?.isInterestOnly || false);
    setIsWaiveLenderFee(historyParams?.isWaiveLenderFee || false);

    // PPE: restore the merged + Non-QM fields too. The `body` in check_rates
    // carries them, but without restoring here they'd reset to each field's
    // useState default whenever the collapsed form is reopened to refine a
    // search. Keys mirror that body; numeric/boolean fields use ?? so a valid
    // 0/false survives. Total loan stays null when absent so it can be derived
    // from the restored loan amounts.
    const historyProgram = historyParams?.loanProgram || "conventional";
    const restoredLien = historyParams?.lienPosition === "second" || isSecondLienProgram(historyProgram)
      ? "second"
      : "first";
    const restoredProgram = restoredLien === "second"
      ? (isSecondLienProgram(historyProgram) ? historyProgram : "secondMortgage")
      : (isSecondLienProgram(historyProgram) ? "conventional" : historyProgram);
    const restoredTerm = String(historyParams?.loanTerm || "");
    if (restoredProgram === "heloc") {
      setLoanTerm(["fixed15", "fixed25", "fixed30"].includes(restoredTerm) ? restoredTerm : "fixed30");
      setPpeAmort("fixed");
    }
    setLoanProgram(restoredProgram);
    // Old saved searches may contain an invalid program/lien pair. Normalize
    // both fields before showing or replaying the request.
    setPpeLien(restoredLien);
    if (restoredProgram === "heloc") {
      setPpeAmort("fixed");
    } else if (restoredTerm.startsWith("arm")) {
      setPpeAmort("arm");
      setPpeArmTerm(ARM_TERM_YEARS[restoredTerm] || historyParams?.armFixedTerm || 5);
    } else {
      setPpeAmort("fixed");
      setPpeArmTerm(historyParams?.armFixedTerm ?? 5);
    }
    setPpeAppraised(historyParams?.appraisedValue ?? null);
    // Restored appraisal values are inactive until Advanced is opened.
    setPpeAppraisalOverrideActive(false);
    setPpeCashOut(historyParams?.cashOutAmount ?? null);
    setPpeCltv(historyParams?.cltvInput ?? null);
    setPpeHcltv(historyParams?.hcltvInput ?? null);
    setPpeLine(historyParams?.lineAmount ?? PROGRAM_FIELD_DEFAULTS.helocLineAmount);
    setPpeDraw(historyParams?.drawAmount ?? PROGRAM_FIELD_DEFAULTS.helocDrawAmount);
    setPpeDrawTermMonths(historyParams?.drawTermMonths ?? PROGRAM_FIELD_DEFAULTS.helocDrawTermMonths);
    setPpePrimaryMtg(
      historyParams?.primaryMortgageAmount
        ?? PROGRAM_FIELD_DEFAULTS.secondLienPrimaryMortgageAmount,
    );
    // Fall back to the useState defaults, not null: rows saved before these had
    // defaults carry no fundingFee/guaranteeFee, and restoring null re-triggers
    // the "loanProgram=va requires fundingFee" rejection on re-run.
    const restoredVaExempt = historyParams?.vaExemptFundingFee || false;
    setPpeFundingFee(
      restoredVaExempt
        ? 0
        : (historyParams?.fundingFee ?? PROGRAM_FIELD_DEFAULTS.vaFundingFee),
    );
    setPpeVaSubseq(historyParams?.vaSubsequentUse || false);
    setPpeVaExempt(restoredVaExempt);
    setPpeGuaranteeFee(historyParams?.guaranteeFee ?? PROGRAM_FIELD_DEFAULTS.usdaGuaranteeFee);
    setPpeReserves(historyParams?.reserves ?? BORROWER_DEFAULTS.reserves);
    setPpePropsFinanced(
      historyParams?.propertiesFinanced ?? BORROWER_DEFAULTS.propertiesFinanced,
    );
    setPpeNonOccupant(historyParams?.nonOccupantCoBorrower || false);
    setPpeItin(historyParams?.itin || false);
    setPpeUnits(historyParams?.numberOfUnits ?? 1);
    // Restored as saved: the ZIP is looked up but State/County are not
    // refilled from it (a mismatch is only flagged under the field).
    setPpeZip(historyParams?.propertyZip || "");
    setZipNote("");
    setPpePrepay(historyParams?.prepaymentPenalty ?? 0);
    setPpeInterestOnly(historyParams?.interestOnlyNew || false);

    const nq = historyParams?.nonQm;
    if (nq) {
      const restoredIncomeDoc = (nq.incomeDocumentation && nq.incomeDocumentation[0]) || "FullDoc";
      const restoredDocumentation = restoredIncomeDoc === "NoRatio" ? "DebtServiceCoverageRatio" : restoredIncomeDoc;
      const restoredDscrMode = (
        restoredIncomeDoc === "DebtServiceCoverageRatio" && nq.dscrMode === "noRatio"
          ? "noRatio"
          : "number"
      );
      if (isSecondLienProgram(restoredProgram)) {
        setSecondIncomeDoc(restoredDocumentation);
        setSecondDscrMode(restoredDscrMode);
        setSecondDscr(nq.dscr ?? 1.2);
      } else {
        setNqIncomeDoc(restoredDocumentation);
        setNqDscrMode(restoredDscrMode);
        setNqDscr(nq.dscr ?? 1.2);
      }
      const restoredHousingEvent = nq.housingEventType || "";
      const restoredBkType = nq.bankruptcyType || "";
      setNqHousingEvent(restoredHousingEvent);
      setNqHousingSeasoning(
        restoredHousingEvent
          ? (normalizeSeasoningBucket(nq.housingEventSeasoning) || DEFAULT_SEASONING_BUCKET)
          : ""
      );
      setNqBkType(restoredBkType);
      setNqBkOutcome(
        restoredBkType
          ? (normalizeBankruptcyOutcome(nq.bankruptcyOutcome) || DEFAULT_BANKRUPTCY_OUTCOME)
          : ""
      );
      setNqBkSeasoning(
        restoredBkType
          ? (normalizeSeasoningBucket(nq.bankruptcySeasoning) || DEFAULT_SEASONING_BUCKET)
          : ""
      );
      // Older saved searches always contain an all-zero mortgageLates object.
      // Restore "Yes" only when at least one actual late was recorded.
      setNqHasMortgageLates(hasMortgageLateCounts(nq.mortgageLates));
      const l12 = nq.mortgageLates?.last12mo || {};
      const l24 = nq.mortgageLates?.m13to24 || {};
      setNqLates({
        l12_30: l12.x30 || 0, l12_60: l12.x60 || 0, l12_90: l12.x90 || 0, l12_120: l12.x120 || 0,
        l24_30: l24.x30 || 0, l24_60: l24.x60 || 0, l24_90: l24.x90 || 0, l24_120: l24.x120 || 0,
      });
      setNqShortTermRental(nq.shortTermRental || false);
      setNqVacant(nq.vacantUnleased || false);
      setNqFirstTimeInvestor(nq.firstTimeInvestor || false);
      setNqEntityVesting(nq.entityVesting || false);
      setNqRural(nq.ruralProperty || false);
      setNqUnique(nq.uniqueProperty || false);
    } else {
      setNqIncomeDoc("FullDoc");
      setNqDscrMode("number");
      setNqDscr(1.2);
      setSecondIncomeDoc("FullDoc");
      setSecondDscrMode("number");
      setSecondDscr(1.2);
      setNqHasMortgageLates(false);
      setNqLates({ l12_30: 0, l12_60: 0, l12_90: 0, l12_120: 0, l24_30: 0, l24_60: 0, l24_90: 0, l24_120: 0 });
    }
  }, [historyParams]);

  useEffect(() => {
    if (!closingFeeConfig) return;

    const lodeStar = lodeStarConfig || {};
    const shouldCalculate =
      lodeStar.enableCanShopServices === false &&
      lodeStar.enableTaxesAndOtherGovernmentFees === false &&
      lodeStar.enableOther === false;

    setIsCalculatedAtResults(!shouldCalculate);

    if (shouldCalculate) {
      const calculateArrayTotal = (arr) => {
        if (!arr) return 0;
        return arr.reduce(
          (sum, item) => sum + (Number(item.estimateAmount) || 0),
          0
        );
      };

      const total =
        calculateArrayTotal(closingFeeConfig.canNotShopServices) +
        calculateArrayTotal(closingFeeConfig.canShopServices) +
        calculateArrayTotal(closingFeeConfig.taxesAndOtherGovernmentFees) +
        calculateArrayTotal(closingFeeConfig.prepaids) +
        calculateArrayTotal(closingFeeConfig.initialEscrowPaymentsAtClosing) +
        calculateArrayTotal(closingFeeConfig.other) +
        calculateArrayTotal(closingFeeConfig.originationCharges);

      setEstimatedClosingFee(total);
    } else {
      setEstimatedClosingFee("Calculated at results");
    }
  }, [lodeStarConfig, closingFeeConfig]);

  // useEffect(() => {
  //   setUpFrontMIP(Number(loanAmount || 0) * 0.0175);
  // }, [loanAmount]);

  const showCounties = () => {
    let options = [
    // <option value="">Select your county</option>
    ];
    if (state && state in loanLimits) {
      let keys = Object.keys(loanLimits[state]);
      const countyOption = (x) => (
        <option key={x} value={x}>{com.capitalize(x.replace("COUNTY", ""))}</option>
      );
      // A known ZIP's counties go first; picking one of the others clears the ZIP.
      const inZip = zipCountiesInState(currentZipCounties(), state, loanLimits);
      if (inZip.length > 0) {
        return [
          <Select.OptGroup key="in-zip" label={`In ZIP ${zip5(ppeZip)}`}>
            {inZip.map(countyOption)}
          </Select.OptGroup>,
          <Select.OptGroup key="other" label="Other counties (clears the ZIP)">
            {keys.filter((x) => !inZip.includes(x)).map(countyOption)}
          </Select.OptGroup>,
        ];
      }
      keys.forEach((x) => {
        options.push(countyOption(x));
      });
    }
    return options;
  };

  // The looked-up counties of the ZIP in the field, or null while unknown.
  const currentZipCounties = () => (
    zipLookup && zipLookup.zip === zip5(ppeZip) ? zipLookup.counties : null
  );

  latestFieldsRef.current = { ppeZip, state, county, loanLimits };

  // Typing a full ZIP fills State and County with its main county, unless the
  // county already shown is one of the ZIP's.
  const updateZip = (value) => {
    setPpeZip(value);
    setZipNote("");
    const zip = zip5(value);
    if (!zip) return;
    lookupZipCounties(zip)
      .then((counties) => {
        const latest = latestFieldsRef.current;
        if (zip5(latest.ppeZip) !== zip) return;
        const next = locationForZip(counties, latest, latest.loanLimits);
        if (!next) {
          setZipNote(`ZIP ${zip} is not in the Census ZIP list; check State and County yourself.`);
          return;
        }
        setState(next.state);
        setCounty(next.county);
      })
      .catch((err) => console.error(err));
  };

  const zipFieldNote = () => {
    const counties = currentZipCounties();
    if (!zipMatchesLocation(counties, state, county)) {
      return (
        <div className="ppe-zip-note ppe-zip-note-error">
          {`ZIP ${zip5(ppeZip)} is in ${counties.map((c) => describeLocation(c.state, c.county)).join(" or ")}, not the county selected.`}
        </div>
      );
    }
    return zipNote ? <div className="ppe-zip-note">{zipNote}</div> : null;
  };

  const closeModal_no_lender = () => {
    setIsOpen_no_lender(false);
    // console.log("closeModal_no_lender");
  };

  const loan_svg = () => {
    return (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="16"
        height="16"
        viewBox="0 0 16 16"
        fill="none"
      >
        <path
          d="M11.1337 5.33333C11.001 4.95683 10.7586 4.62864 10.4378 4.39102C10.1171 4.1534 9.73249 4.01722 9.33366 4H6.66699C6.13656 4 5.62785 4.21071 5.25278 4.58579C4.87771 4.96086 4.66699 5.46957 4.66699 6C4.66699 6.53043 4.87771 7.03914 5.25278 7.41421C5.62785 7.78929 6.13656 8 6.66699 8H9.33366C9.86409 8 10.3728 8.21071 10.7479 8.58579C11.1229 8.96086 11.3337 9.46957 11.3337 10C11.3337 10.5304 11.1229 11.0391 10.7479 11.4142C10.3728 11.7893 9.86409 12 9.33366 12H6.66699C6.26816 11.9828 5.8836 11.8466 5.56281 11.609C5.24203 11.3714 4.99969 11.0432 4.86699 10.6667M8.00033 2V4M8.00033 12V14"
          stroke="#1630AC"
          stroke-width="1.5"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>
    );
  };

  const property_svg = () => {
    return (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="16"
        height="16"
        viewBox="0 0 16 16"
        fill="none"
      >
        <path
          d="M2.55556 8H1L8 1L15 8H13.4444"
          stroke="#1630AC"
          stroke-width="1.4"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
        <path
          d="M2.55556 8V13.4444C2.55556 13.857 2.71944 14.2527 3.01117 14.5444C3.30289 14.8361 3.69855 15 4.11111 15H11.8889C12.3014 15 12.6971 14.8361 12.9888 14.5444C13.2806 14.2527 13.4444 13.857 13.4444 13.4444V8"
          stroke="#1630AC"
          stroke-width="1.4"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
        <path
          d="M6.44444 8H9.55556V11.1111H6.44444V8Z"
          stroke="#1630AC"
          stroke-width="1.4"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>
    );
  };

  const borrower_svg = () => {
    return (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="16"
        height="16"
        viewBox="0 0 16 16"
        fill="none"
      >
        <path
          d="M4 14V12.6667C4 11.9594 4.28095 11.2811 4.78105 10.781C5.28115 10.281 5.95942 10 6.66667 10H9.33333C10.0406 10 10.7189 10.281 11.219 10.781C11.719 11.2811 12 11.9594 12 12.6667V14M5.33333 4.66667C5.33333 5.37391 5.61428 6.05219 6.11438 6.55229C6.61448 7.05238 7.29276 7.33333 8 7.33333C8.70724 7.33333 9.38552 7.05238 9.88562 6.55229C10.3857 6.05219 10.6667 5.37391 10.6667 4.66667C10.6667 3.95942 10.3857 3.28115 9.88562 2.78105C9.38552 2.28095 8.70724 2 8 2C7.29276 2 6.61448 2.28095 6.11438 2.78105C5.61428 3.28095 5.33333 3.95942 5.33333 4.66667Z"
          stroke="#1630AC"
          stroke-width="1.5"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>
    );
  };

  const product_svg = () => {
    return (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="18"
        height="16"
        viewBox="0 0 18 16"
        fill="none"
      >
        <path
          fill-rule="evenodd"
          clip-rule="evenodd"
          d="M7.0918 3.50059C7.0918 2.58932 7.83396 1.85059 8.74947 1.85059H9.2518C10.1673 1.85059 10.9095 2.58932 10.9095 3.50059V9.83392C10.9095 10.7452 10.1673 11.4839 9.2518 11.4839H8.74947C7.83396 11.4839 7.0918 10.7452 7.0918 9.83392V3.50059ZM8.74947 3.15059C8.55527 3.15059 8.39784 3.30729 8.39784 3.50059V9.83392C8.39784 10.0272 8.55527 10.1839 8.74947 10.1839H9.2518C9.446 10.1839 9.60342 10.0272 9.60342 9.83392V3.50059C9.60342 3.30729 9.446 3.15059 9.2518 3.15059H8.74947Z"
          fill="#1630AC"
        />
        <path
          fill-rule="evenodd"
          clip-rule="evenodd"
          d="M2.31934 6.00059C2.31934 5.08932 3.0615 4.35059 3.97701 4.35059H4.47934C5.39484 4.35059 6.13701 5.08932 6.13701 6.00059V10.0006C6.13701 10.9119 5.39484 11.6506 4.47934 11.6506H3.97701C3.0615 11.6506 2.31934 10.9119 2.31934 10.0006V6.00059ZM3.97701 5.65059C3.78281 5.65059 3.62538 5.80729 3.62538 6.00059V10.0006C3.62538 10.1939 3.78281 10.3506 3.97701 10.3506H4.47934C4.67353 10.3506 4.83096 10.1939 4.83096 10.0006V6.00059C4.83096 5.80729 4.67353 5.65059 4.47934 5.65059H3.97701Z"
          fill="#1630AC"
        />
        <rect
          x="16.0322"
          y="12.5"
          width="1.335"
          height="14.0651"
          rx="0.6675"
          transform="rotate(90 16.0322 12.5)"
          fill="#1630AC"
        />
        <path
          fill-rule="evenodd"
          clip-rule="evenodd"
          d="M11.8643 7.50059C11.8643 6.58932 12.6064 5.85059 13.5219 5.85059H14.0243C14.9398 5.85059 15.6819 6.58932 15.6819 7.50059V10.0006C15.6819 10.9119 14.9398 11.6506 14.0243 11.6506H13.5219C12.6064 11.6506 11.8643 10.9119 11.8643 10.0006V7.50059ZM13.5219 7.15059C13.3277 7.15059 13.1703 7.30729 13.1703 7.50059V10.0006C13.1703 10.1939 13.3277 10.3506 13.5219 10.3506H14.0243C14.2185 10.3506 14.3759 10.1939 14.3759 10.0006V7.50059C14.3759 7.30729 14.2185 7.15059 14.0243 7.15059H13.5219Z"
          fill="#1630AC"
        />
      </svg>
    );
  };

  const question_svg = () => {
    return (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="14"
        height="14"
        viewBox="0 0 14 14"
        fill="none"
      >
        <path
          d="M7 0.00330764C5.20252 -0.0545737 3.45674 0.649163 2.14481 1.96047C0.832876 3.27178 0.0616697 5.08384 0 7C0.0616697 8.91616 0.832876 10.7282 2.14481 12.0395C3.45674 13.3508 5.20252 14.0546 7 13.9967C8.79748 14.0546 10.5433 13.3508 11.8552 12.0395C13.1671 10.7282 13.9383 8.91616 14 7C13.9383 5.08384 13.1671 3.27178 11.8552 1.96047C10.5433 0.649163 8.79748 -0.0545737 7 0.00330764ZM7 12.8306C5.49252 12.8886 4.02483 12.3078 2.91799 11.2152C1.81114 10.1225 1.15523 8.60693 1.09375 7C1.15523 5.39307 1.81114 3.87751 2.91799 2.78485C4.02483 1.69218 5.49252 1.11135 7 1.16942C8.50747 1.11135 9.97517 1.69218 11.082 2.78485C12.1889 3.87751 12.8448 5.39307 12.9062 7C12.8448 8.60693 12.1889 10.1225 11.082 11.2152C9.97517 12.3078 8.50747 12.8886 7 12.8306Z"
          fill="#586570"
        />
        <path
          d="M7.02836 10.461C7.42048 10.461 7.73836 10.1435 7.73836 9.75197C7.73836 9.3604 7.42048 9.04297 7.02836 9.04297C6.63624 9.04297 6.31836 9.3604 6.31836 9.75197C6.31836 10.1435 6.63624 10.461 7.02836 10.461Z"
          fill="#586570"
        />
        <path
          d="M8.51312 3.97133C8.07969 3.6172 7.54523 3.43274 6.99937 3.44891C6.73589 3.43256 6.47199 3.47211 6.22294 3.56527C5.9739 3.65844 5.74467 3.80336 5.54854 3.99165C5.3524 4.17994 5.19326 4.40785 5.08032 4.66218C4.96739 4.91651 4.90291 5.1922 4.89062 5.47328V5.5759H5.97562V5.48261C5.99362 5.21038 6.11081 4.95647 6.30212 4.77516C6.49344 4.59385 6.74369 4.49954 6.99937 4.5124C7.12278 4.4965 7.24795 4.50881 7.36654 4.54851C7.48514 4.58822 7.59444 4.65441 7.68716 4.74267C7.77989 4.83094 7.8539 4.93925 7.90429 5.06041C7.95468 5.18156 7.98027 5.31277 7.97937 5.4453C7.97937 5.77181 7.76937 6.12631 7.29688 6.48081C7.0628 6.62105 6.86212 6.81684 6.71039 7.05302C6.55866 7.28921 6.45994 7.55945 6.42187 7.84283V8.17867H7.50687V7.93612C7.51478 7.75601 7.58674 7.58588 7.70813 7.46034C7.84642 7.29352 7.99872 7.14051 8.16312 7.00323L8.31187 6.87262C8.55546 6.70381 8.75579 6.473 8.8948 6.20102C9.03381 5.92904 9.10713 5.62443 9.10812 5.31469C9.10903 5.05767 9.05608 4.80367 8.95314 4.57126C8.8502 4.33884 8.69987 4.13388 8.51312 3.97133Z"
          fill="#586570"
        />
      </svg>
    );
  };

  const edit_svg = () => {
    return (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="17"
        height="16"
        viewBox="0 0 17 16"
        fill="none"
      >
        <path
          fill-rule="evenodd"
          clip-rule="evenodd"
          d="M2.96245 3.47627C2.77885 3.47627 2.60278 3.5492 2.47296 3.67902C2.34314 3.80884 2.27021 3.98491 2.27021 4.1685V13.8598C2.27021 14.0434 2.34314 14.2195 2.47296 14.3493C2.60278 14.4791 2.77885 14.552 2.96245 14.552H12.6537C12.8373 14.552 13.0134 14.4791 13.1432 14.3493C13.273 14.2195 13.346 14.0434 13.346 13.8598V11.0909C13.346 10.7085 13.6559 10.3986 14.0382 10.3986C14.4205 10.3986 14.7304 10.7085 14.7304 11.0909V13.8598C14.7304 14.4106 14.5116 14.9388 14.1222 15.3282C13.7327 15.7177 13.2045 15.9365 12.6537 15.9365H2.96245C2.41167 15.9365 1.88345 15.7177 1.49399 15.3282C1.10454 14.9388 0.885742 14.4106 0.885742 13.8598V4.1685C0.885742 3.61773 1.10454 3.08951 1.49399 2.70005C1.88345 2.31059 2.41167 2.0918 2.96245 2.0918H5.73139C6.1137 2.0918 6.42362 2.40172 6.42362 2.78403C6.42362 3.16634 6.1137 3.47627 5.73139 3.47627H2.96245Z"
          fill="#325CEB"
        />
        <path
          fill-rule="evenodd"
          clip-rule="evenodd"
          d="M12.0257 0.910759C12.2961 0.640424 12.7343 0.640424 13.0047 0.910759L15.9121 3.81814C16.1814 4.08748 16.1825 4.5238 15.9146 4.79454L9.3384 11.44C9.21586 11.5638 9.05113 11.6369 8.87709 11.6446L5.76203 11.7831C5.56529 11.7918 5.37413 11.7164 5.23642 11.5756C5.09872 11.4348 5.0275 11.242 5.0406 11.0455L5.24827 7.93043C5.25942 7.76312 5.33092 7.60555 5.44949 7.48699L12.0257 0.910759ZM12.5152 2.37921L6.61237 8.28204L6.47346 10.3656L8.54492 10.2736L14.4462 4.31018L12.5152 2.37921Z"
          fill="#325CEB"
        />
      </svg>
    );
  };

  // 添加新的状态变量
  const [cltv, setCltv] = useState(0);
  const [hcltv, setHcltv] = useState(0);

  // 添加新的更新函数
  const updateCltv = (value) => {
    setCltv(value);
  };

  const updateHcltv = (value) => {
    setHcltv(value);
  };

  // PPE: CLTV/HCLTV are "everything secured by the property / property value".
  // The two differ only for a HELOC subordinate: CLTV counts the active DRAW,
  // HCLTV counts the FULL credit line (undrawn portion included), because the
  // borrower can draw the rest at any time. With no subordinate financing all
  // three ratios collapse to LTV, which is why this went unnoticed.
  //
  // Single source of truth: both the inputs below and the request body read this,
  // so what the form shows is always what gets priced.
  const getDerivedLoanMetrics = () => calculateLoanMetrics({
    loanProgram,
    loanPurpose,
    propertyValue,
    appraisedValue: getEffectivePpeAppraisedValue(),
    loanAmount: isSecondLienProgram(loanProgram)
      ? loanAmount
      : getDerivedFirstLienAmounts().baseLoanAmount,
    secondaryLoanAmount,
    primaryMortgageAmount: ppePrimaryMtg,
    lineAmount: ppeLine,
    drawAmount: ppeDraw,
    drawTermMonths: ppeDrawTermMonths,
  });

  const getDerivedRatios = () => getDerivedLoanMetrics();

  const getEffectiveRatios = () => resolveLoanMetricOverrides({
    lienPosition: ppeLien,
    derivedMetrics: getDerivedRatios(),
    cltvOverride: ppeCltv,
    hcltvOverride: ppeHcltv,
  });

  const mixedUseTooltip = () => {
    return (
      <div>
        <p><span style={{ cursor: 'pointer', textDecoration: 'underline'}} onClick={() => {
          window.open('https://www.consumerfinance.gov/rural-or-underserved-tool/', '_blank');
        }}>click to check</span> if the property is mixed use</p>
      </div>
    );
  };

  const field_Loan = () => {
    const fmt = (value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    const prs = (value) => value.replace(/\$\s?|(,*)/g, "");
    const derivedMetrics = getDerivedLoanMetrics();
    const secondLien = isSecondLienProgram(loanProgram);
    return (
      <div className="search-fields-content-item loan_field">
        <div className="search-fields-content-item-title">
          <div className="search-fields-content-item-title-svg">{loan_svg()}</div>
          <span className="search-fields-content-item-title-text">Loan</span>
        </div>
        <div className="search-fields-content-item-content">
          <div className="search-fields-content-item-content-item">
            <div className="search-fields-content-item-content-item-title">*Lien position</div>
            <Select
              style={{ width: "100%", borderRadius: 6, height: 44 }}
              value={ppeLien}
              onChange={updatePpeLien}
              options={[
                { value: "first", label: "First lien" },
                { value: "second", label: "Second lien" },
              ]}
            />
          </div>
          <div className="search-fields-content-item-content-item">
            <div className="search-fields-content-item-content-item-title">*Loan program</div>
            <Select
              style={{ width: "100%", borderRadius: 6, height: 44 }}
              value={loanProgram}
              onChange={updateLoanProgram}
              options={LOAN_PROGRAM_OPTIONS_BY_LIEN[ppeLien]}
            />
          </div>
          <div className="search-fields-content-item-content-item">
            <div className="search-fields-content-item-content-item-title">*Loan purpose</div>
            <Select
              style={{ width: "100%", borderRadius: 6, height: 44 }}
              value={loanPurpose}
              onChange={updateLoanPurpose}
              options={LOAN_PURPOSE_OPTIONS}
            />
          </div>
          <div className="search-fields-content-item-content-item">
            <div className="search-fields-content-item-content-item-title">*Amortization</div>
            <Select disabled={loanProgram === "heloc"} style={{ width: "100%", borderRadius: 6, height: 44 }} value={ppeAmort} onChange={updateAmortization}>
              <option value="fixed">Fixed</option>
              <option value="arm">ARM</option>
            </Select>
          </div>
          <div className="search-fields-content-item-content-item">
            <div className="search-fields-content-item-content-item-title">*Loan term</div>
            <Select style={{ width: "100%", borderRadius: 6, height: 44 }} value={loanTerm} onChange={updateLoanTerm}>
              {(loanProgram === "heloc" ? HELOC_TERM_OPTIONS : (ppeAmort === "arm" ? ARM_TERM_OPTIONS : FIXED_TERM_OPTIONS)).map(({ value, label }) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </Select>
          </div>
          {loanProgram === "heloc" && (
            <div className="search-fields-content-item-content-item">
              <div className="search-fields-content-item-content-item-title">*Draw term</div>
              <Select style={{ width: "100%", borderRadius: 6, height: 44 }} value={ppeDrawTermMonths} onChange={setPpeDrawTermMonths}>
                <option value={24}>2 years (24 months)</option>
                <option value={36}>3 years (36 months)</option>
                <option value={60}>5 years (60 months)</option>
                <option value={120}>10 years (120 months)</option>
              </Select>
            </div>
          )}
          {loanProgram === "heloc" && (
            <div className="second-lien-terms-note">
              <span className="second-lien-terms-note-title">HELOC structure</span>
              <span className="second-lien-terms-note-copy">Variable-rate line; draw term is separate from the 15, 25, or 30-year repayment term</span>
            </div>
          )}
          <div className="search-fields-content-item-content-item">
            <div className="search-fields-content-item-content-item-title">
              {loanPurpose === "Purchase" ? "*Property value" : "*Home value"}
            </div>
            <div className="search-fields-content-item-content-item-select" style={{ display: "flex", flexDirection: "column" }}>
              <InputNumber prefix="$" style={{ width: "100%", borderRadius: 6, height: 44 }} placeholder="Input"
                value={propertyValue} onChange={updatePropertyValue} status={propteryError ? "error" : ""} formatter={fmt} parser={prs} />
              {propteryError && <div style={{ color: "#C21818", fontSize: "14px", marginTop: "4px", width: "100%", textAlign: "left" }}>{propteryError}</div>}
            </div>
          </div>
          {secondLien && (
            <div className="search-fields-content-item-content-item">
              <div className="search-fields-content-item-content-item-title">Appraised value</div>
              <InputNumber prefix="$" style={{ width: "100%", borderRadius: 6, height: 44 }} value={ppeAppraised ?? propertyValue} onChange={updatePpeAppraised} formatter={fmt} parser={prs} />
            </div>
          )}
          {secondLien && (
            <div className="loan-structure-section">
              <div className="loan-structure-section-title">Loan structure</div>
              <div className="loan-structure-section-copy">
                {loanProgram === "heloc" ? "Loan Balance pricing uses line amount minus draw; the full line is used for HCLTV and the draw amount for CLTV." : "The second-lien balance is included in both CLTV and HCLTV."}
              </div>
            </div>
          )}
          {loanProgram === "secondMortgage" && (
            <div className="search-fields-content-item-content-item">
              <div className="search-fields-content-item-content-item-title">*Loan amount</div>
              <InputNumber style={{ width: "100%", borderRadius: 6, height: 44 }} prefix="$" placeholder="Loan amount" value={loanAmount} onChange={updateLoanAmount} formatter={fmt} parser={prs} />
            </div>
          )}
          {!isSecondLienProgram(loanProgram) && (
          <div className="search-fields-content-item-content-item">
            <div className="search-fields-content-item-content-item-title">
              <div style={{ display: "flex", width: "100%" }}>
                <span style={{ flex: 2 }}>{loanPurpose === "Purchase" ? "Down payment（1st）" : "*Loan amount（1st）"}</span>
                <span style={{ flex: 1 }}>LTV</span>
              </div>
            </div>
            <div className="search-fields-content-item-content-item-select" style={{ display: "flex", border: "1px solid #d9d9d9", borderRadius: "6px", overflow: "hidden" }}>
              {loanPurpose === "Purchase" ? (
                <InputNumber style={{ flex: 2, border: "none", borderRadius: 0, height: 44 }} prefix="$" placeholder="Down payment" value={downPayment} onChange={updateDownPayment} formatter={fmt} parser={prs} />
              ) : (
                <InputNumber style={{ flex: 2, border: "none", borderRadius: 0, height: 44 }} prefix="$" placeholder="Loan amount" value={loanAmount} onChange={updateLoanAmount} formatter={fmt} parser={prs} />
              )}
              <div style={{ width: "1px", height: "34px", backgroundColor: "#d9d9d9" }}></div>
              <InputNumber style={{ flex: 1, border: "none", borderRadius: 0, height: 44 }} placeholder="LTV" suffix="%" value={getDerivedLoanMetrics().ltv} onChange={updateLtv} />
            </div>
          </div>
          )}
          {loanProgram === "fha" && (
            <div className="search-fields-content-item-content-item">
              <div className="search-fields-content-item-content-item-title">UFMIP</div>
              <InputNumber style={{ width: "100%", borderRadius: 6, height: 44 }} suffix="%" value={upFrontMIP} onChange={setUpFrontMIP} />
            </div>
          )}
          {loanProgram === "va" && (
            <>
              <div className="search-fields-content-item-content-item">
                <div className="search-fields-content-item-content-item-title">Funding Fee</div>
                <InputNumber
                  style={{ width: "100%", borderRadius: 6, height: 44 }}
                  suffix="%"
                  min={ppeVaExempt ? 0 : 0.01}
                  disabled={ppeVaExempt}
                  value={ppeFundingFee}
                  onChange={setPpeFundingFee}
                  onBlur={() => {
                    if (!ppeVaExempt && !(Number(ppeFundingFee) > 0)) {
                      message.error("Funding Fee must be greater than 0 unless VA Exempt Funding Fee is selected.");
                      setPpeFundingFee(PROGRAM_FIELD_DEFAULTS.vaFundingFee);
                    }
                  }}
                />
              </div>
              <div className="search-fields-content-item-content-item"><Checkbox checked={ppeVaSubseq} onChange={(e) => setPpeVaSubseq(e.target.checked)}>VA Subsequent Use</Checkbox></div>
              <div className="search-fields-content-item-content-item"><Checkbox checked={ppeVaExempt} onChange={updateVaExemptFundingFee}>VA Exempt Funding Fee</Checkbox></div>
            </>
          )}
          {loanProgram === "usda" && (
            <div className="search-fields-content-item-content-item">
              <div className="search-fields-content-item-content-item-title">USDA G-Fee</div>
              <InputNumber style={{ width: "100%", borderRadius: 6, height: 44 }} suffix="%" value={ppeGuaranteeFee} onChange={setPpeGuaranteeFee} />
            </div>
          )}
          {loanProgram === "heloc" && (
            <div className="search-fields-content-item-content-item">
              <div className="search-fields-content-item-content-item-title">*Line amount</div>
              <InputNumber
                prefix="$"
                min={0}
                status={ppeHelocAmountError ? "error" : ""}
                style={{ width: "100%", borderRadius: 6, height: 44 }}
                value={ppeLine}
                onChange={(value) => {
                  setPpeLine(value);
                  validatePpeHelocAmounts(value, ppeDraw);
                }}
                formatter={fmt}
                parser={prs}
              />
            </div>
          )}
          {loanProgram === "heloc" && (
            <div className="search-fields-content-item-content-item">
              <div className="search-fields-content-item-content-item-title">Draw</div>
              <InputNumber
                prefix="$"
                min={0}
                max={Number(ppeLine) || 0}
                status={ppeHelocAmountError ? "error" : ""}
                style={{ width: "100%", borderRadius: 6, height: 44 }}
                value={ppeDraw}
                onChange={(value) => {
                  setPpeDraw(value);
                  validatePpeHelocAmounts(ppeLine, value);
                }}
                formatter={fmt}
                parser={prs}
              />
              {ppeHelocAmountError && (
                <div style={{ color: "#C21818", fontSize: "14px", marginTop: "4px", width: "100%", textAlign: "left" }}>
                  {ppeHelocAmountError}
                </div>
              )}
            </div>
          )}
          {(loanProgram === "secondMortgage" || ppeLien === "second") && (
            <div className="search-fields-content-item-content-item">
              <div className="search-fields-content-item-content-item-title">{loanPurpose === "Purchase" ? "First Mortgage Amount" : "Existing First Mortgage Balance"}</div>
              <InputNumber prefix="$" style={{ width: "100%", borderRadius: 6, height: 44 }} value={ppePrimaryMtg} onChange={setPpePrimaryMtg} formatter={fmt} parser={prs} />
            </div>
          )}
          {secondLien && (
            <div className="search-fields-content-item-content-item second-lien-ratio-field">
              <div className="search-fields-content-item-content-item-title">CLTV</div>
              <InputNumber
                suffix="%"
                controls={false}
                readOnly
                style={{ width: "100%", borderRadius: 6, height: 44 }}
                value={derivedMetrics.cltv}
              />
            </div>
          )}
          {loanProgram === "heloc" && (
            <div className="search-fields-content-item-content-item second-lien-ratio-field">
              <div className="search-fields-content-item-content-item-title">HCLTV</div>
              <InputNumber
                suffix="%"
                controls={false}
                readOnly
                style={{ width: "100%", borderRadius: 6, height: 44 }}
                value={derivedMetrics.hcltv}
              />
            </div>
          )}
          {loanPurpose === "CashOutRefi" && (
            <div className="search-fields-content-item-content-item">
              <div className="search-fields-content-item-content-item-title">*Cash-Out</div>
              <InputNumber
                prefix="$"
                min={0}
                style={{ width: "100%", borderRadius: 6, height: 44 }}
                value={ppeCashOut}
                onChange={updatePpeCashOut}
                status={ppeCashOutError ? "error" : ""}
                formatter={fmt}
                parser={prs}
              />
              {ppeCashOutError && <div style={{ color: "#C21818", fontSize: "14px", marginTop: "4px", width: "100%", textAlign: "left" }}>{ppeCashOutError}</div>}
            </div>
          )}
          <AdvancedLoanFields
            lienPosition={ppeLien}
            open={ppeAdvOpen}
            onToggle={toggleAdvanced}
            appraisedValue={getEffectivePpeAppraisedValue()}
            totalLoanAmount={getDerivedFirstLienAmounts().totalLoanAmount}
            secondaryLoanAmount={secondaryLoanAmount}
            cltv={getEffectiveRatios().cltv}
            hcltv={getEffectiveRatios().hcltv}
            onAppraisedValueChange={updatePpeAppraised}
            onTotalLoanAmountChange={setPpeTotalLoan}
            onSecondaryLoanAmountChange={updateSecondaryLoanAmount}
            onCltvChange={setPpeCltv}
            onHcltvChange={setPpeHcltv}
          />
        </div>
      </div>
    );
  };

  const field_Property = () => {
    return (
      <div className="search-fields-content-item property_field">
        <div className="search-fields-content-item-title">
          <div className="search-fields-content-item-title-svg">
            {property_svg()}
          </div>
          <span className="search-fields-content-item-title-text">
            Property
          </span>
        </div>
        <div className="search-fields-content-item-content">
          <div className="search-fields-content-item-content-item">
            <div className="search-fields-content-item-content-item-title">
              Occupancy
            </div>
            <div className="search-fields-content-item-content-item-select">
              <Select
                style={{ width: "100%", borderRadius: 6, height: 44 }}
                type="text"
                placeholder="Select"
                value={occupancy}
                onChange={updateOccupancy}
              >
                <option value="principal">Primary Residence</option>
                <option value="secondhome">Second Home</option>
                <option value="investment">Investment</option>
              </Select>
            </div>
          </div>
          <div className="search-fields-content-item-content-item">
            <div className="search-fields-content-item-content-item-title">
              Property Type
            </div>
            <div className="search-fields-content-item-content-item-select">
              <Select
                style={{ width: "100%", borderRadius: 6, height: 44 }}
                type="text"
                placeholder="Select"
                value={propertyType}
                onChange={updatePropertyType}
              >
                <option value="singlefamilydetached">
                  Single Family Home, Detached
                </option>
                <option value="singlefamilyattached">
                  Single Family Home, Attached
                </option>
                <option value="condominiumdetached">
                  Condominium, Detached
                </option>
                <option value="condominium">Condominium, Attached</option>
                <option value={NON_WARRANTABLE_CONDO_PROPERTY_TYPE}>Condominium, Non-Warrantable</option>
                <option value="pudattached">
                  Planned Unit Development, attached
                </option>
                <option value="puddetached">
                  Planned Unit Development, detached
                </option>
                <option value="twotofourfamily">2 to 4 Unit Multifamily</option>
                <option value="manufacturedhome">Manufactured Home</option>
              </Select>
            </div>
          </div>
          <div className="search-fields-content-item-content-item">
            <div className="search-fields-content-item-content-item-title">*Units</div>
            <InputNumber className="ppe-num" style={{ width: "100%", borderRadius: 6, height: 44 }} min={1} value={ppeUnits} onChange={setPpeUnits} />
          </div>
          <div className="search-fields-content-item-content-item">
            <div className="search-fields-content-item-content-item-title">
              State
            </div>
            <div className="search-fields-content-item-content-item-select">
              <Select
                style={{ width: "100%", borderRadius: 6, height: 44 }}
                type="text"
                placeholder="Select"
                value={state}
                onChange={updateState}
              >
                <option value="ALABAMA">Alabama</option>
                <option value="ALASKA">Alaska</option>
                <option value="ARIZONA">Arizona</option>
                <option value="ARKANSAS">Arkansas</option>
                <option value="CALIFORNIA">California</option>
                <option value="COLORADO">Colorado</option>
                <option value="CONNECTICUT">Connecticut</option>
                <option value="DELAWARE">Delaware</option>
                <option value="FLORIDA">Florida</option>
                <option value="GEORGIA">Georgia</option>
                <option value="HAWAII">Hawaii</option>
                <option value="IDAHO">Idaho</option>
                <option value="ILLINOIS">Illinois</option>
                <option value="INDIANA">Indiana</option>
                <option value="IOWA">Iowa</option>
                <option value="KANSAS">Kansas</option>
                <option value="KENTUCKY">Kentucky</option>
                <option value="LOUISIANA">Louisiana</option>
                <option value="MAINE">Maine</option>
                <option value="MARYLAND">Maryland</option>
                <option value="MASSACHUSETTS">Massachusetts</option>
                <option value="MICHIGAN">Michigan</option>
                <option value="MINNESOTA">Minnesota</option>
                <option value="MISSISSIPPI">Mississippi</option>
                <option value="MISSOURI">Missouri</option>
                <option value="MONTANA">Montana</option>
                <option value="NEBRASKA">Nebraska</option>
                <option value="NEVADA">Nevada</option>
                <option value="NEW HAMPSHIRE">New Hampshire</option>
                <option value="NEW JERSEY">New Jersey</option>
                <option value="NEW MEXICO">New Mexico</option>
                <option value="NEW YORK">New York</option>
                <option value="NORTH CAROLINA">North Carolina</option>
                <option value="NORTH DAKOTA">North Dakota</option>
                <option value="OHIO">Ohio</option>
                <option value="OKLAHOMA">Oklahoma</option>
                <option value="OREGON">Oregon</option>
                <option value="PENNSYLVANIA">Pennsylvania</option>
                <option value="RHODE ISLAND">Rhode Island</option>
                <option value="SOUTH CAROLINA">South Carolina</option>
                <option value="SOUTH DAKOTA">South Dakota</option>
                <option value="TENNESSEE">Tennessee</option>
                <option value="TEXAS">Texas</option>
                <option value="UTAH">Utah</option>
                <option value="VERMONT">Vermont</option>
                <option value="VIRGINIA">Virginia</option>
                <option value="WASHINGTON">Washington</option>
                <option value="WEST VIRGINIA">West Virginia</option>
                <option value="WISCONSIN">Wisconsin</option>
                <option value="WYOMING">Wyoming</option>
              </Select>
            </div>
          </div>
          <div className="search-fields-content-item-content-item">
            <div className="search-fields-content-item-content-item-title">
              County
            </div>
            <div className="search-fields-content-item-content-item-select">
              <Select
                style={{ width: "100%", borderRadius: 6, height: 44 }}
                type="text"
                placeholder="Select county"
                value={county}
                onChange={updateCounty}
              >
                {showCounties()}
              </Select>
            </div>
          </div>
          {/* <div className="search-fields-content-item-content-item">
            <Checkbox checked={isRural} onChange={updateRural}>
              Rural
              {
                <Tooltip title="Click to check if the property qualifies as rural">
                  <span className="search-fields-content-item-content-item-svg">
                    {question_svg()}
                  </span>
                </Tooltip>
              }
            </Checkbox>
            <Checkbox checked={isMixedUse} onChange={updateMixedUse}>
              Mixed Use
              {
                <Tooltip title={mixedUseTooltip()}>
                  <span className="search-fields-content-item-content-item-svg">
                    {question_svg()}
                  </span>
                </Tooltip>
              }
            </Checkbox>
          </div> */}
          <div className="search-fields-content-item-content-item">
            <div className="search-fields-content-item-content-item-title">Property Zip</div>
            <Input style={{ width: "100%", borderRadius: 6, height: 44 }} value={ppeZip} onChange={(e) => updateZip(e.target.value)} placeholder="Zip" />
            {zipFieldNote()}
          </div>
        </div>
      </div>
    );
  };
  const field_Borrower = () => {
    return (
      <div className="search-fields-content-item borrower_field">
        <div className="search-fields-content-item-title">
          <div className="search-fields-content-item-title-svg">
            {borrower_svg()}
          </div>
          <span className="search-fields-content-item-title-text">
            Borrower
          </span>
        </div>
        <div className="search-fields-content-item-content">
          <div className="search-fields-content-item-content-item">
            <div className="search-fields-content-item-content-item-title">
              Est FICO
            </div>
            <div 
              className="search-fields-content-item-content-item-select" 
              style={{
                display: "flex",
                flexDirection: "column",
              }}>
              <InputNumber
                suffix=" "
                style={{ width: "100%", height: 44, borderRadius: 6, border: ficoError ? "1px solid #C21818" : "1px solid #d9d9d9" }}
                placeholder="Input"
                value={fico}
                onChange={updateFico}
                onBlur={() => {}}
                status={ficoError ? "error" : ""}
              />
              {ficoError && (
                <div style={{ color: "#C21818", fontSize: "14px", marginTop: "4px", width: "100%", textAlign: "left" }}>
                  {ficoError}
                </div>
              )}
            </div>
          </div>
          <div className="search-fields-content-item-content-item">
            <div className="search-fields-content-item-content-item-title">
              Monthly income
            </div>
            <div className="search-fields-content-item-content-item-select">
              <InputNumber
                prefix="$"
                style={{ width: "100%", height: 44 }}
                placeholder="Input"
                value={monthlyIncome}
                onChange={updateMonthlyIncome}
                formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                parser={(value) => value.replace(/\$\s?|(,*)/g, '')}
              />
            </div>
          </div>
          <div className="search-fields-content-item-content-item">
            <div className="search-fields-content-item-content-item-title">
              <div style={{ display: "flex", width: "100%" }}>
                <span style={{ flex: 2 }}>Monthly debt</span>
                <span style={{ flex: 1 }}>DTI</span>
              </div>
            </div>
            <div
              className="search-fields-content-item-content-item-select"
              style={{
                display: "flex",
                border: "1px solid #d9d9d9",
                borderRadius: "6px",
                overflow: "hidden",
              }}
            >
              <InputNumber
                style={{ flex: 2, border: "none", borderRadius: 0, height: 44 }}
                prefix="$"
                placeholder="Monthly debt"
                value={monthlyDebt}
                onChange={updateMonthlyDebt}
                formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                parser={(value) => value.replace(/\$\s?|(,*)/g, '')}
              />
              {/* <div
                style={{
                  width: "1px",
                  height: "34px",
                  backgroundColor: "#d9d9d9",
                }}
              ></div> */}
              <InputNumber
                style={{ flex: 1, border: "none", borderRadius: 0, height: 44 }}
                disabled
                placeholder="DTI"
                suffix="%"
                min={0}
                max={100}
                precision={2}
                value={dti}
              />
            </div>
          </div>
          <div className="search-fields-content-item-content-item">
            <div className="search-fields-content-item-content-item-title">Reserves (mo)</div>
            <InputNumber className="ppe-num" style={{ width: "100%", borderRadius: 6, height: 44 }} min={0} value={ppeReserves} onChange={setPpeReserves} />
          </div>
          <div className="search-fields-content-item-content-item">
            <div className="search-fields-content-item-content-item-title">Properties Financed</div>
            <InputNumber className="ppe-num" style={{ width: "100%", borderRadius: 6, height: 44 }} min={0} value={ppePropsFinanced} onChange={setPpePropsFinanced} />
          </div>
          <div className="search-fields-content-item-content-item">
            <div className="search-fields-content-item-content-item-title">
              *Citizenship
            </div>
            <div className="search-fields-content-item-content-item-select">
              <Select
                style={{ width: "100%", borderRadius: 6, height: 44 }}
                type="text"
                placeholder="Select"
                value={citizenship}
                onChange={updateCitizenship}
              >
                <option value="US_CITIZENS">US Citizen</option>
                <option value="PERMANENT_RESIDENT_ALIEN">
                  Permanent Resident Alien
                </option>
                <option value="NON_RESIDENT_ALIEN">Non Resident Alien</option>
              </Select>
            </div>
          </div>
          <div className="search-fields-content-item-content-item">
            <Checkbox
              checked={isFirstTimeHomeBuyer}
              onChange={updateFirstTimeHomeBuyer}
            >
              First time home buyer
            </Checkbox>
          </div>
          <div className="search-fields-content-item-content-item">
            <Checkbox checked={ppeNonOccupant} onChange={(e) => setPpeNonOccupant(e.target.checked)}>Non-occupant co-borrower</Checkbox>
          </div>
          <div className="search-fields-content-item-content-item">
            <Checkbox checked={ppeItin} onChange={(e) => setPpeItin(e.target.checked)}>ITIN</Checkbox>
          </div>
          <div className="search-fields-content-item-content-item">
            <Checkbox checked={isSelfEmployed} onChange={updateSelfEmployed}>
              Self employed
            </Checkbox>
          </div>
        </div>
      </div>
    );
  };

  const handleEditCompensation = () => {
    setAddLenderModal(true);
  };

  const handleEditCompensationModal = () => {
    console.log("handleEditCompensationModal");
  };

  const reset_all = () => {
    // loan
    setLoanPurpose("Purchase");
    setLoanTerm("fixed30");
    setLoanType(["conventional"]);
    setLoanProgram("conventional");
    setPpeLien("first");
    setPpeAmort("fixed");
    setPpeArmTerm(5);
    setPropertyValue(FIRST_LIEN_PURCHASE_DEFAULTS.propertyValue);
    setDownPayment(FIRST_LIEN_PURCHASE_DEFAULTS.downPayment);
    setLoanAmount(FIRST_LIEN_PURCHASE_DEFAULTS.loanAmount);
    setUpFrontMIP(1.75);
    setUpFrontMIP_Financed(true);
    setSecondaryLoanAmount(FIRST_LIEN_PURCHASE_DEFAULTS.secondaryLoanAmount);
    setIsSubordinateLiens(false);
    setLienType();
    setLienPosition();
    setHelocLineAmount(0);
    setHelocDrawAmount(0);
    setHelocLtv(0);
    setCltv(0);
    setHcltv(0);
    setPpeAppraised(null);
    setPpeAppraisalOverrideActive(false);
    setPpeTotalLoan(null);
    setPpeCashOut(null);
    setPpeCltv(null);
    setPpeHcltv(null);
    setPpeLine(PROGRAM_FIELD_DEFAULTS.helocLineAmount);
    setPpeDraw(PROGRAM_FIELD_DEFAULTS.helocDrawAmount);
    setPpePrimaryMtg(PROGRAM_FIELD_DEFAULTS.secondLienPrimaryMortgageAmount);

    // property
    setOccupancy("principal");
    setPropertyType("singlefamilydetached");
    setPpeUnits(1); // keep Units coherent with the property type being reset
    setPpeZip("");
    setZipNote("");
    setState("CALIFORNIA");
    setCounty("ALAMEDA COUNTY");
    setIsRural(false);
    setIsMixedUse(false);

    // borrower
    setFico(780);
    setMonthlyIncome(10000);
    setMonthlyDebt(1000);
    setDti(calculateDti(10000, 1000));
    setPpeReserves(BORROWER_DEFAULTS.reserves);
    setPpePropsFinanced(BORROWER_DEFAULTS.propertiesFinanced);
    setPpeNonOccupant(false);
    setPpeItin(false);
    setCitizenship("US_CITIZENS");
    setIsFirstTimeHomeBuyer(true);
    setIsSelfEmployed(false);

    // product
    setSearchBy("price");
    setTargetPrice(100);
    setTargetRate(null);
    setTargetPriceType([""]);
    setLockDay(30);
    setCompensationType("lender_paid");
    setCompensationPercent(0);
    setEstimatedClosingFee(0);
    setAusType([]);
    setBuydown("none");
    setIsImpound(true);
    setIsInterestOnly(false);
    setIsWaiveLenderFee(false);
    setPpePrepay(0);
    setPpeInterestOnly(false);
    setPpeAdvOpen(false);
    setNqIncomeDoc("FullDoc");
    setNqDscrMode("number");
    setNqDscr(1.2);
    setSecondIncomeDoc("FullDoc");
    setSecondDscrMode("number");
    setSecondDscr(1.2);
    setNqHasMortgageLates(false);
    setNqLates({ l12_30: 0, l12_60: 0, l12_90: 0, l12_120: 0, l24_30: 0, l24_60: 0, l24_90: 0, l24_120: 0 });
    setPpeFundingFee(PROGRAM_FIELD_DEFAULTS.vaFundingFee);
    setPpeVaSubseq(false);
    setPpeVaExempt(false);
    setPpeGuaranteeFee(PROGRAM_FIELD_DEFAULTS.usdaGuaranteeFee);

    // reset error
    setPropteryError('');
    setFicoError('');
  };

  const updateSearchBy = (value) => {
    setSearchBy(value);
    setTargetRateMessage('');
    if(value === 'rate'){
      setTargetRate(null);
    }
  };

  const updateTargetRate = (value) => {
    if(value === null){
      setTargetRateMessage('Please enter target rate');
      // return;
    }else if(value < 0 || value > 20){
      setTargetRateMessage('Target rate must be between 0 and 20');
      // return;
    }else{
      setTargetRateMessage('');
    }
    setTargetRate(value);
  };

  const field_Product = () => {
    return (
      <div className="search-fields-content-item product_field">
        <div className="search-fields-content-item-title">
          <div className="search-fields-content-item-title-svg">
            {product_svg()}
          </div>
          <span className="search-fields-content-item-title-text">Product</span>
        </div>
        <div className="search-fields-content-item-content">
          <div className="search-fields-content-item-content-item">
            <div className="search-fields-content-item-content-item-title">
              Search by
            </div>
            <div
              className="search-fields-content-item-content-item-select"
              style={{
                display: "flex",
                border: "1px solid #d9d9d9",
                borderRadius: "6px",
                overflow: "hidden",
              }}
            >
              <Select
                style={{
                  flex: 1,
                  border: "none",
                  borderRadius: 0,
                }}
                className="borderNoneSelect"
                type="text"
                placeholder="Select"
                value={searchBy}
                onChange={updateSearchBy}
              >
                <option value="price">Price (Base 100)</option>
                <option value="rate">Rate</option>
              </Select>
            </div>
          </div>
          {searchBy === "price" && (
            <div className="search-fields-content-item-content-item">
              <div className="search-fields-content-item-content-item-title">
                Target price (Base 100)
                <span className="search-fields-content-item-content-item-svg cursor-pointer">
                  <Tooltip title="This filter helps identify rates matching your target lender pricing by calculating the current target price from the sum of lock point, adjustment point, compensation point">
                    {question_svg()}
                  </Tooltip>
                </span>
              </div>
              <div
                className="search-fields-content-item-content-item-select"
                style={{
                  display: "flex",
                  border: targetRateMessage ? "1px solid #C21818" : "1px solid #d9d9d9",
                  borderRadius: "6px",
                  overflow: "hidden",
                }}
              >
                <InputNumber
                  suffix=" "
                  style={{ flex: 1, border: "none", borderRadius: 0, height: 44 }}
                  // min={searchBy === "price" ? 80 : 0}
                  // max={searchBy === "price" ? 120 : 20}
                  type="number"
                  placeholder="Enter target price"
                  value={targetPrice}
                  status={targetRateMessage ? "error" : ""}
                  onChange={updateTargetPrice}
                  formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                  parser={(value) => value.replace(/\$\s?|(,*)/g, '')}
                />
                <div
                  style={{
                    width: "1px",
                    height: "34px",
                    backgroundColor: "#d9d9d9",
                  }}
                ></div>
                <Select
                  style={{
                    flex: 2,
                    border: "none",
                    borderRadius: 0,
                  }}
                  className="borderNoneSelect"
                  type="text"
                  placeholder="Select"
                  mode="multiple"
                  maxTagCount={1}
                  maxTagPlaceholder={() =>
                    targetPriceType.length > 1
                      ? "+" + (targetPriceType.length - 1)
                      : ""
                  }
                  maxTagTextLength={4}
                  value={targetPriceType}
                  onChange={updateTargetPriceType}
                >
                  <option value="">ALL</option>
                  <option value="lockpoint">Lock point</option>
                  <option value="adjustment">Adjustment</option>
                  <option value="compensation">Compensation</option>
                </Select>
              </div>
              {targetRateMessage && <div style={{ color: "#C21818", fontSize: "14px", marginTop: "4px", width: "100%", textAlign: "left" }}>{targetRateMessage}</div>}
            </div>
          )}
          {searchBy === "rate" && (
            <div className="search-fields-content-item-content-item">
              <div className="search-fields-content-item-content-item-title">
                Target rate
              </div>
              <div
                className="search-fields-content-item-content-item-select"
                style={{
                  display: "flex",
                  border: targetRateMessage ? "1px solid #C21818" : "1px solid #d9d9d9",
                  borderRadius: "6px",
                  overflow: "hidden",
                }}
              >
                <InputNumber
                  // min={0}
                  // max={20}
                  style={{ flex: 1, border: "none", borderRadius: 0, height: 44 }}
                  type="number"
                  placeholder="Enter target rate"
                  suffix="%"
                  value={targetRate}
                  status={targetRateMessage ? "error" : ""}
                  onChange={updateTargetRate}
                />
              </div>
              {targetRateMessage && <div style={{ color: "#C21818", fontSize: "14px", marginTop: "4px", width: "100%", textAlign: "left" }}>{targetRateMessage}</div>}
            </div>
          )}
          <div className="search-fields-content-item-content-item">
            <div className="search-fields-content-item-content-item-title">
              Lock days
            </div>
            <div className="search-fields-content-item-content-item-select">
              <Select
                style={{ width: "100%", borderRadius: 6, height: 44 }}
                type="text"
                placeholder="Select"
                value={lockDay}
                onChange={updateLockDay}
              >
                <option value="12">12</option>
                <option value="15">15</option>
                <option value="30">30</option>
                <option value="45">45</option>
                <option value="60">60</option>
                <option value="75">75</option>
              </Select>
            </div>
          </div>
          <div className="search-fields-content-item-content-item">
            <div className="search-fields-content-item-content-item-title">
              Compensation
              {/* <span
                className="search-fields-content-item-content-item-svg cursor-pointer"
                onClick={handleEditCompensation}
              >
                {edit_svg()}
              </span> */}
            </div>
            <div
              className="search-fields-content-item-content-item-select"
              style={{
                display: "flex",
                border: "1px solid #d9d9d9",
                borderRadius: "6px",
                overflow: "hidden",
              }}
            >
              <Select
                style={{
                  flex: 2,
                  border: "none",
                  borderRadius: 0,
                }}
                className="borderNoneSelect"
                type="text"
                placeholder="Select"
                value={compensationType}
                onChange={updateCompensationType}
              >
                <option value="lender_paid">Lender paid</option>
                <option value="borrower_paid">Borrower paid</option>
              </Select>
              {compensationType !== "lender_paid" && <div
                style={{
                  width: "1px",
                  height: "34px",
                  backgroundColor: "#d9d9d9",
                }}
              ></div>}
              {compensationType !== "lender_paid" && <InputNumber
                style={{ flex: 1, border: "none", borderRadius: 0, height: 44 }}
                disabled={compensationType === "lender_paid"}
                placeholder="Input"
                suffix="%"
                value={compensationPercent}
                onChange={updateCompensationPercent}
              />}
            </div>
            {compensationType === "lender_paid" && !checkAccess([UR_Owner]) &&
              <div style={{ color: "#6E6E70", fontSize: 14, marginTop: 8, width: "100%", textAlign: "left", fontWeight: 400 }}>
                Percentage is set by account administrator.
              </div>
            }
            {compensationType === "lender_paid" && checkAccess([UR_Owner]) &&
              <div style={{ color: "#6E6E70", fontSize: 14, marginTop: 8, width: "100%", textAlign: "left", fontWeight: 400 }}>
                Percentage is set in <span style={{ color: "#325CEB", cursor: "pointer", fontWeight: 400, marginLeft: 4 }} onClick={handleEditCompensation}>Lender settings{svg_expand()}</span>
              </div>
            }
          </div>
          <div className="search-fields-content-item-content-item">
            <div className="search-fields-content-item-content-item-title">
              Estimated closing fee
              {/* <span
                className="search-fields-content-item-content-item-svg cursor-pointer"
                onClick={handleEditCostDetails}
              >
                {edit_svg()}
              </span> */}
            </div>
            <div className="search-fields-content-item-content-item-select">
              {isCalculatedAtResults ? (
                <div
                  className="form-control"
                  style={{
                    background: "#ECECEC",
                    color: "#6E6E70",
                    fontFamily: "Inter",
                    fontWeight: 400,
                    height: 44,
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  {/* <span style={{ marginRight: 8 }}>$</span> */}
                  Fees shown on results page
                </div>
              ) : (
                <InputNumber
                  style={{ borderRadius: 6, height: 44, width: "100%" }}
                  prefix="$"
                  placeholder="Input"
                  disabled={true}
                  value={estimatedClosingFee}
                  onChange={updateEstimatedClosingFee}
                  formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                  parser={(value) => value.replace(/\$\s?|(,*)/g, '')}
                />
              )}
            </div>
            {/* {isCalculatedAtResults && (
                <div
                  style={{
                    color: "#6E6E70",
                    fontFamily: "Inter",
                    fontSize: 12,
                    lineHeight: "normal",
                    marginTop: 2,
                  }}
                >
                  *The estimate will be shown on the results page.
                </div>
              )} */}
            {checkAccess([UR_Owner]) && (
              <div style={{ color: "#325CEB", fontSize: 14, marginTop: 8, width: "100%", textAlign: "left", fontWeight: 400 }} 
                className="cursor-pointer"
                onClick={handleEditCostDetails}
              >
                Edit fees{svg_expand()}
              </div>
            )}
            {!checkAccess([UR_Owner]) && (
              <div style={{ color: "#6E6E70", fontSize: 14, marginTop: 8, width: "100%", textAlign: "left", fontWeight: 400 }}>
                Closing fee is set by account administrator.
              </div>
            )}
          </div>
          <div className="search-fields-content-item-content-item">
            <div className="search-fields-content-item-content-item-title">Prepay Penalty (yr)</div>
            <Select style={{ width: "100%", borderRadius: 6, height: 44 }} value={ppePrepay} onChange={setPpePrepay}>
              <option value={0}>None</option>
              <option value={1}>1</option>
              <option value={2}>2</option>
              <option value={3}>3</option>
              <option value={4}>4</option>
              <option value={5}>5</option>
            </Select>
          </div>
          <div className="search-fields-content-item-content-item">
            <div className="search-fields-content-item-content-item-title">
              Buydown
            </div>
            <div className="search-fields-content-item-content-item-select">
              <Select
                style={{ width: "100%", borderRadius: 6, height: 44 }}
                type="text"
                placeholder="None"
                value={buydown}
                allowClear={true}
                onChange={updateBuydown}
              >
                <option value="none">None</option>
                <option value="3/2/1">3/2/1</option>
                <option value="2/1">2/1</option>
                <option value="1/1">1/1</option>
                <option value="1/0">1/0</option>
              </Select>
            </div>
          </div>
          <div className="search-fields-content-item-content-item">
            <div className="search-fields-content-item-content-item-title">
              AUS type
            </div>
            <div className="search-fields-content-item-content-item-select">
              <Select
                style={{ width: "100%", borderRadius: 6, height: 44 }}
                type="text"
                placeholder="Select"
                mode="multiple"
                maxTagCount={1}
                maxTagPlaceholder={(omitted) =>
                  omitted?.length > 0 ? "+" + omitted.length : ""
                }
                allowClear={true}
                value={ausType}
                onChange={updateAusType}
              >
                <option value="DU">DU</option>
                <option value="LP">LP</option>
                <option value="Manual">Manual UW</option>
              </Select>
            </div>
          </div>
          {isSecondLienProgram(loanProgram) && (
            <>
              <div className="search-fields-content-item-content-item ppe-income-documentation-field">
                <div className="search-fields-content-item-content-item-title">*Income documentation</div>
                <Select
                  style={{ width: "100%", borderRadius: 6, height: 44 }}
                  value={secondIncomeDoc}
                  onChange={(value) => {
                    setSecondIncomeDoc(value);
                    if (value === "DebtServiceCoverageRatio") setSecondDscrMode("number");
                  }}
                  options={INCOME_DOCUMENTATION_OPTIONS}
                />
              </div>
              {secondIncomeDoc === "DebtServiceCoverageRatio" && (
                <div className="search-fields-content-item-content-item ppe-dscr-field">
                  <div className="search-fields-content-item-content-item-title">*DSCR</div>
                  <div className={`ppe-dscr-choice ${secondDscrMode === "noRatio" ? "is-no-ratio" : "is-number"}`}>
                    <InputNumber
                      className="ppe-num ppe-dscr-value"
                      aria-label="DSCR value"
                      placeholder="Enter DSCR"
                      value={secondDscrMode === "number" ? secondDscr : null}
                      disabled={secondDscrMode === "noRatio"}
                      onFocus={() => setSecondDscrMode("number")}
                      onChange={(value) => {
                        setSecondDscrMode("number");
                        setSecondDscr(value ?? 1.2);
                      }}
                      min={0.01}
                      step={0.05}
                    />
                    <Button
                      className="ppe-dscr-no-ratio"
                      type={secondDscrMode === "noRatio" ? "primary" : "default"}
                      aria-pressed={secondDscrMode === "noRatio"}
                      onClick={() => setSecondDscrMode(secondDscrMode === "noRatio" ? "number" : "noRatio")}
                    >
                      No Ratio
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
          <div className="search-fields-content-item-content-item">
            <Checkbox checked={ppeInterestOnly} onChange={(e) => setPpeInterestOnly(e.target.checked)}>Interest Only</Checkbox>
          </div>
          <div className="search-fields-content-item-content-item">
            <Checkbox checked={isImpound} onChange={updateImpound}>
              Impound
            </Checkbox>
            {/* <Checkbox checked={isInterestOnly} onChange={updateInterestOnly}>
              Interest only
            </Checkbox> */}
            {/* <Checkbox
              checked={isWaiveLenderFee}
              onChange={updateWaiveLenderFee}
            >
              Waive Lender Fee?
            </Checkbox> */}
          </div>
        </div>
      </div>
    );
  };

  const getLender = () => {
    setIsLoading(true);
    const token = sessionStorage.getItem('ZeitroA')
    fetch('/data/getcustomerlenders', {
      method: 'GET',
      headers: {
          Authorization: 'Bearer ' + token,
          Cache: 'no-cache',
      },
  })
      .then((response) => {
          if (response.status !== 200) {
              console.warn(
                  'Looks like there was a problem. Status Code: ' +
                      response.status
              )
              return
          }
          // Examine the text in the response
          response.json().then((js) => {
              if (js.Status === 'OK') {
                  if (js.Lenders !== null) {
                      const hasLenders =
                          js.Lenders.some((item) => item.Active) ||
                          js.Lenders.some((item) => item.Hidden)
                      if (hasLenders) {
                          setHasLenders(true)
                      }
                  }
              }
          }).finally(() => {
            setIsLoading(false)
          })
      })
      .catch(function (err) {
          console.error('Fetch lenders Error : ', err)
      })
  }

  const check_property_value = (type, value) => {
    let propertyValue_ = propertyValue;
    let loanAmount_ = loanProgram === "heloc" ? ppeLine : loanAmount;
    let downPayment_ = downPayment;
    if(type === 'propertyValue'){
     propertyValue_ = value
    }
    if(type === 'loanAmount'){
      loanAmount_ = value
    }
    if(type === 'downPayment'){
      downPayment_ = value
    }
    if (!(Number(propertyValue_) > 0)) {
      setPropteryError('Home value must be greater than $0.');
      return false;
    }
    if (loanPurpose === 'Purchase' && !isSecondLienProgram(loanProgram)) {
      if(propertyValue_ < downPayment_){
        // message.error('Property value must be greater than down payment.');
        setPropteryError('Property value must be greater than down payment.');
        return false;
      }else{
        setPropteryError('');
      }
    }else{
      if(propertyValue_ < loanAmount_){
        // message.error('Property value must be greater than loan amount.');
        setPropteryError('Property value must be greater than loan amount.');
        return false;
      }else{
        setPropteryError('');
      }
    }
  }

  const check_rates = () => {
    const derivedRatios = getDerivedLoanMetrics();
    const validation = validatePpeSearch({
      loanPurpose,
      loanProgram,
      propertyValue,
      appraisedValue: ppeAppraised,
      appraisalActive: ppeAdvOpen && ppeAppraisalOverrideActive,
      downPayment,
      loanAmount: loanProgram === "heloc" ? ppeDraw : getDerivedFirstLienAmounts().baseLoanAmount,
      lineAmount: ppeLine,
      drawAmount: ppeDraw,
      drawTermMonths: ppeDrawTermMonths,
      primaryMortgageAmount: ppePrimaryMtg,
      cashOutAmount: ppeCashOut,
      secondaryLoanAmount,
      advancedOpen: ppeAdvOpen,
      totalLoanAmount: ppeAdvOpen ? getDerivedFirstLienAmounts().totalLoanAmount : undefined,
      cltvOverride: ppeCltv,
      hcltvOverride: ppeHcltv,
      ltv: derivedRatios.ltv,
      cltv: getEffectiveRatios().cltv,
      hcltv: getEffectiveRatios().hcltv,
      fico,
      units: ppeUnits,
      propertyType,
      state,
      county,
      zip: ppeZip,
      zipCounties: currentZipCounties(),
      monthlyIncome,
      monthlyDebt,
      reserves: ppeReserves,
      propertiesFinanced: ppePropsFinanced,
      lockDays: lockDay,
      searchBy,
      targetPrice,
      targetRate,
      targetPriceType,
      compensationType,
      compensationPercent,
      fundingFee: ppeFundingFee,
      vaExempt: ppeVaExempt,
      guaranteeFee: ppeGuaranteeFee,
      requiresDscr: INCOME_DOCUMENTATION_PROGRAMS.includes(loanProgram)
        && (isSecondLienProgram(loanProgram) ? secondIncomeDoc : nqIncomeDoc) === "DebtServiceCoverageRatio",
      dscrMode: isSecondLienProgram(loanProgram) ? secondDscrMode : nqDscrMode,
      dscr: isSecondLienProgram(loanProgram) ? secondDscr : nqDscr,
      lateCounts: Object.values(nqLates),
    });
    if (!validation.valid) {
      const first = validation.firstError;
      if (first.field === "targetPrice" || first.field === "targetRate" || first.field === "targetPriceType") setTargetRateMessage(first.message);
      if (first.field === "fico") setFicoError(first.message);
      if (["cashOutAmount"].includes(first.field)) setPpeCashOutError(first.message);
      if (["lineAmount", "drawAmount"].includes(first.field)) setPpeHelocAmountError(first.message);
      if (["propertyValue", "downPayment"].includes(first.field)) setPropteryError(first.message);
      message.error(first.message);
      return;
    }
    setTargetRateMessage("");
    setFicoError("");

    if (loanProgram === "va" && !ppeVaExempt && !(Number(ppeFundingFee) > 0)) {
      message.error("Funding Fee must be greater than 0 unless VA Exempt Funding Fee is selected.");
      return;
    }

    if (loanProgram === "heloc" && !validatePpeHelocAmounts(ppeLine, ppeDraw)) {
      message.error("Draw amount cannot exceed the line amount.");
      return;
    }

    if(!hasLenders){
      setIsOpen_no_lender(true);
      return;
    }
    if(check_property_value() === false){return};
    // 获取用户信息
    const savedUserInfo = JSON.parse(
      sessionStorage.getItem("statsig_user_info")
    );
    const customerid = com.getCustomerId();
    const secondLienProgram = isSecondLienProgram(loanProgram);
    const firstLienAmounts = getDerivedFirstLienAmounts();
    const subjectLoanAmount = loanProgram === "heloc"
      ? Number(ppeDraw) || 0
      : Number(loanAmount) || 0;

    // 构建请求体
    const body = {

      Rate_CRAAdjustment: false, // flag
      Rate_LMIBAdjustment: false, // flag
      Rate_LMITAdjustment: false, // flag
      Rate_MMCTAdjustment: false, // flag
      appraisalWaiver: false, // flag
      borrowerPaidCompensationAmount: 0, // flag
      currentJobInMonths: 24, // flag
      familyRelatedOrSmallBusiness: false, // flag
      getFiles: false, // flag
      giftFundUsed: false, // flag
      // monthlyDebt: 1000, // flag
      // monthlyIncome: 10000, // flag
      pmi: 0.51, // flag
      point: [{lender: "default", percent: 1, fixed: 500}], // flag
      rsuAsIncome: -1, // flag
      unit: 1, // flag
      visaType: "H_1B", // flag

      // loan
      loanProgram: loanProgram, // PPE: explicit merged program selector
      // PPE: additional unified-pricing fields.
      lienPosition: ppeLien,
      amortizationType: ppeAmort,
      armFixedTerm: ppeArmTerm,
      appraisedValue: getEffectivePpeAppraisedValue(),
      totalLoanAmount: secondLienProgram ? undefined : firstLienAmounts.totalLoanAmount,
      cashOutAmount: loanPurpose === "CashOutRefi" ? ppeCashOut : undefined,
      cltvInput: ppeCltv,
      hcltvInput: ppeHcltv,
      lineAmount: loanProgram === "heloc" ? ppeLine : undefined,
      drawAmount: loanProgram === "heloc" ? ppeDraw : undefined,
      drawTermMonths: loanProgram === "heloc" ? ppeDrawTermMonths : undefined,
      primaryMortgageAmount: secondLienProgram ? ppePrimaryMtg : undefined,
      fundingFee: ppeVaExempt ? undefined : ppeFundingFee,
      vaSubsequentUse: ppeVaSubseq,
      vaExemptFundingFee: ppeVaExempt,
      guaranteeFee: ppeGuaranteeFee,
      reserves: ppeReserves,
      propertiesFinanced: ppePropsFinanced,
      nonOccupantCoBorrower: ppeNonOccupant,
      itin: ppeItin,
      numberOfUnits: ppeUnits,
      propertyZip: ppeZip,
      prepaymentPenalty: ppePrepay,
      interestOnlyNew: ppeInterestOnly,
      purpose: loanPurpose,
      loanTerm: loanTerm,
      loanTypes: loanType,
      propertyValue: propertyValue, // new
      downPayment: !secondLienProgram && loanPurpose === "Purchase" ? downPayment : undefined,
      // Purchase collects downPayment in the UI, so derive the loan amount at
      // request time (mirrors the legacy flow) instead of trusting the state.
      loanAmount: secondLienProgram
        ? subjectLoanAmount
        : firstLienAmounts.baseLoanAmount,
      upFrontMIP: upFrontMIP,
      upFrontMIP_Financed: upFrontMIP_Financed,
      LTV: derivedRatios.ltv,
      // Derived from the subordinate financing actually entered (see
      // getDerivedRatios); the ppeCltv/ppeHcltv inputs override when filled in.
      CLTV: getEffectiveRatios().cltv,
      HCLTV: getEffectiveRatios().hcltv,
      secondaryLoanAmount: secondLienProgram ? undefined : secondaryLoanAmount, // new
      // isSubordinateLiens: isSubordinateLiens, // new

      // property
      occupancy: com.getOccupancy(occupancy),
      property: com.getPropertyType(propertyType === NON_WARRANTABLE_CONDO_PROPERTY_TYPE ? "condominium" : propertyType),
      nonWarrantableCondo: propertyType === NON_WARRANTABLE_CONDO_PROPERTY_TYPE ? true : undefined,
      state: state,
      county: county,
      isRural: isRural, // new
      isMixedUseProperty: isMixedUse, // new

      // borrower
      FICO: fico,
      monthlyIncome: parseFloat(monthlyIncome),
      monthlyDebt: parseFloat(monthlyDebt),
      dti: dti,
      citizenship: citizenship,
      firstTimeHomeBuyer: isFirstTimeHomeBuyer,
      selfEmployed: isSelfEmployed,

      // product
      searchBy: searchBy, // new
      targetPrice: targetPrice, // new
      targetRate: targetRate, // new
      targetPriceType: targetPriceType, // new
      lockDay: lockDay,
      compensationType: compensationType, // new
      compensationPercent: compensationPercent, // new
      estimatedClosingFee: parseFloat(estimatedClosingFee), 
      ausTypes: ausType, // new
      buydown: buydown, // new

      isImpound: isImpound, // new
      escrowWaiver: !isImpound, // new

      isInterestOnly: isInterestOnly, // new
      isWaiveLenderFee: isWaiveLenderFee, // new
    };

    // PPE: Non-QM keeps its full details block; both second-lien programs reuse
    // its income documentation/DSCR inputs because they use the Non-QM engine.
    if (INCOME_DOCUMENTATION_PROGRAMS.includes(loanProgram)) {
      const incomeDocumentation = isSecondLienProgram(loanProgram) ? secondIncomeDoc : nqIncomeDoc;
      const dscrMode = isSecondLienProgram(loanProgram) ? secondDscrMode : nqDscrMode;
      const dscr = isSecondLienProgram(loanProgram) ? secondDscr : nqDscr;
      const nonQm = {
        incomeDocumentation: [incomeDocumentation],
      };
      if (incomeDocumentation === "DebtServiceCoverageRatio") {
        nonQm.dscrMode = dscrMode;
        if (dscrMode === "number") nonQm.dscr = Number(dscr);
      }
      if (loanProgram === "nonqm") {
        if (nqHasMortgageLates) {
          nonQm.mortgageLates = {
            last12mo: { x30: nqLates.l12_30, x60: nqLates.l12_60, x90: nqLates.l12_90, x120: nqLates.l12_120 },
            m13to24: { x30: nqLates.l24_30, x60: nqLates.l24_60, x90: nqLates.l24_90, x120: nqLates.l24_120 },
          };
        }
        if (nqHousingEvent) {
          nonQm.housingEventType = nqHousingEvent;
          if (nqHousingSeasoning) nonQm.housingEventSeasoning = nqHousingSeasoning;
        }
        if (nqBkType) {
          nonQm.bankruptcyType = nqBkType;
          if (nqBkOutcome) nonQm.bankruptcyOutcome = nqBkOutcome;
          if (nqBkSeasoning) nonQm.bankruptcySeasoning = nqBkSeasoning;
        }
        if (nqShortTermRental) nonQm.shortTermRental = true;
        if (nqVacant) nonQm.vacantUnleased = true;
        if (nqFirstTimeInvestor) nonQm.firstTimeInvestor = true;
        if (nqEntityVesting) nonQm.entityVesting = true;
        if (nqRural) nonQm.ruralProperty = true;
        if (nqUnique) nonQm.uniqueProperty = true;
      }
      body.nonQm = nonQm;
    }

    // 调用父组件的回调函数
    onCheckRates(body, savedUserInfo, customerid);
  };

  const handleEditCostDetails = () => {
    setShowEditCostDetails(true);
  };

  const handleEditCostDetailsModal = () => {
    setShowEditCostDetails(false);
  };
  let setEstClosingfee = (lodeStarConfig, closingFeeConfig) => {

    let data = {
        lodeStarConfig: {
            ...lodeStarConfig,
            isModified: true,
            enableCanNotShopServices: false,
            enablePrepaids: false,
            enableInitialEscrowPaymentsAtClosing: false
        },
        closingCostsDetail: closingFeeConfig
    }
    let token = sessionStorage.getItem("ZeitroA");
    fetch("/los/setloanestimatedclosingfee", {
        cache: "no-cache",
        method: "POST",
        headers: {
            Authorization: "Bearer " + token,
            "Content-Type": "application/json",
            Cache: "no-cache",
        },
        body: JSON.stringify(data)
    }).then(response => {
        if (!response.ok) {
        } else {
            setEstimatedClosingFee(totalClosingFee)
            getloanestimatedclosingfee()
        }
    }).catch(error => {
    })
  }

  const handleLodestarChange = (checked) => {
    let newLodeStarConfig = {
      ...lodeStarConfig,
      enableCanNotShopServices: checked,
      enableCanShopServices: checked,
      enableTaxesAndOtherGovernmentFees: checked,
      enablePrepaids: checked,
      enableInitialEscrowPaymentsAtClosing: checked,
      enableOther: checked,
    }
    const closingFee = closingCostRef.current.getClosingFee()
    setEstClosingfee(newLodeStarConfig, closingFee)
  };

  const handleConfigChange = (newConfig) => {
    const closingFee = closingCostRef.current.getClosingFee()
    setEstClosingfee(newConfig, closingFee)
  };

  const handleSaveClosingFee = () => {
    setEstimatedClosingFee(totalClosingFee);
    const closingFee = closingCostRef.current.getClosingFee();
    setEstClosingfee(lodeStarConfig, closingFee)
    handleEditCostDetailsModal();
  };

  const getEditCostsModal = () => {
    return (
      <Modal
        className="edit-fees-modal"
        centered
        destroyOnClose
        maskClosable={true}
        width={974}
        open={showEditCostDetails}
        title=""
        onCancel={handleEditCostDetailsModal}
        footer={null}
      >
        <div className="edit-fees-modal-title">Closing cost details</div>
        <div className="edit-fees-modal-content edit-fees-modal-content-new">
          <div style={{ marginBottom: 12 }}>
            <LodestarTipsCard
              initialConfig={lodeStarConfig}
              onLodestarChange={handleLodestarChange}
            />
          </div>
          <div className="estimatedclosingfee-container">
            <ClosingCost
              ref={closingCostRef}
              initialConfig={lodeStarConfig}
              initialClosingFee={closingFeeConfig}
              onConfigChange={handleConfigChange}
              onTotalFeesChange={(totalFees) => setTotalClosingFee(totalFees)}
            />
          </div>
          <div className="edit-fees-modal-footer">
            <Button
              className="edit-fees-modal-cancel"
              onClick={handleEditCostDetailsModal}
              variant="customize"
            >
              Cancel
            </Button>
            <Button
              className="edit-fees-modal-save"
              variant="primary"
              onClick={handleSaveClosingFee}
            >
              Save
            </Button>
          </div>
        </div>
      </Modal>
    );
  };

  const [addLenderModal, setAddLenderModal] = useState(false);

  const handleCloseAddLenderModal = () => {
    setAddLenderModal(false);
  };

  const AddLenderModal = () => {
    return (
      <Modal
        open={addLenderModal}
        onCancel={handleCloseAddLenderModal}
        centered
        footer={[
          <Button key="back" onClick={handleCloseAddLenderModal}>
            Cancel
          </Button>,
        ]}
        width={1200}
      >
        <Lenders addLenders={addLenders} statsigSource="rates"/>
      </Modal>
    );
  };
  const svg_collapse = () => {
    return (
      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
        <path fill-rule="evenodd" clip-rule="evenodd" d="M17.8107 10.5607L16.75 9.5L12.1544 14.0956L7.56066 9.50184L6.5 10.5625L11.0937 15.1562L11.0931 15.1569L12.1538 16.2175L12.1544 16.2169L12.1569 16.2194L13.2175 15.1587L13.2151 15.1563L17.8107 10.5607Z" fill="#222222"/>
      </svg>
    )
  }

  const backToResult = () => {
    onBackToResult();
  }

  const addLenders = (has) => {
    setHasLenders(has);
    setIsOpen_no_lender(false);
    closeModal_no_lender();
    
    if (has) {
      setShouldCheckRatesAfterLenderUpdate(true);
    }
  }
  // (removed: legacy loanType->UFMIP dollar recompute; upFrontMIP is now a
  // percent and only sent for loanProgram=fha at request-build time)

  useEffect(() => {
    if (shouldCheckRatesAfterLenderUpdate && hasLenders) {
      setShouldCheckRatesAfterLenderUpdate(false);
      check_rates();
    }
  }, [hasLenders, shouldCheckRatesAfterLenderUpdate]);

  const seasoningOptions = (
    <>
      <option value="lt12">0-11 mo</option>
      <option value="12-23">12-23 mo</option>
      <option value="24-35">24-35 mo</option>
      <option value="36-47">36-47 mo</option>
      <option value="48-59">48-59 mo</option>
      <option value="60-83">60-83 mo</option>
      <option value="gte84">84+ mo</option>
    </>
  );

  // PPE: full-width Non-QM details section (shown when loanProgram === "nonqm").
  const field_NonQM = () => (
    <div className="ppe-nonqm-section">
      <div className="ppe-nonqm-head"><span className="ppe-nonqm-icon">✦</span> NON-QM DETAILS</div>
      <div className="ppe-nonqm-grid">
        <div className="ppe-nonqm-primary-fields">
          <div className="ppe-nonqm-field">
            <div className="ppe-nonqm-label">*Income documentation</div>
            <Select
              style={{ width: "100%", borderRadius: 6, height: 44 }}
              value={nqIncomeDoc}
              onChange={(value) => {
                setNqIncomeDoc(value);
                if (value === "DebtServiceCoverageRatio") setNqDscrMode("number");
              }}
            >
              <option value="DebtServiceCoverageRatio">DSCR</option>
              <option value="FullDoc">Full Doc</option>
              <option value="FullDoc12Mo">Full Doc 12mo</option>
              <option value="FullDocWithAssets">Full Doc with Assets</option>
              <option value="PersonalBankStatement12Mo">Personal Bank Stmt 12mo</option>
              <option value="PersonalBankStatement24Mo">Personal Bank Stmt 24mo</option>
              <option value="BusinessBankStatement12Mo">Business Bank Stmt 12mo</option>
              <option value="BusinessBankStatement24Mo">Business Bank Stmt 24mo</option>
              <option value="BankStatement3Mo">Bank Stmt 3mo</option>
              <option value="BusinessBankStatement6Mo">Business Bank Stmt 6mo</option>
              <option value="Form109912Mo">1099 12mo</option>
              <option value="Form109924Mo">1099 24mo</option>
              <option value="CPAPL">CPA P&amp;L</option>
              <option value="PL">P&amp;L (borrower prepared)</option>
              <option value="PL12">P&amp;L 12mo (borrower prepared)</option>
              <option value="PL24">P&amp;L 24mo (borrower prepared)</option>
              <option value="AssetDepletion">Asset Depletion</option>
              <option value="AssetUtilization">Asset Utilization</option>
              <option value="AssetQualifier">Asset Qualifier</option>
              <option value="AssetBasedIncome">Asset Based Income</option>
              <option value="VOE">WVOE</option>
              <option value="VOE24Mo">2-year VOE</option>
            </Select>
          </div>
          {nqIncomeDoc === "DebtServiceCoverageRatio" && <div className="ppe-nonqm-field">
              <div className="ppe-nonqm-label">*DSCR</div>
              <div className={`ppe-dscr-choice ${nqDscrMode === "noRatio" ? "is-no-ratio" : "is-number"}`}>
                <InputNumber
                  className="ppe-num ppe-dscr-value"
                  aria-label="DSCR value"
                  placeholder="Enter DSCR"
                  value={nqDscrMode === "number" ? nqDscr : null}
                  onFocus={() => setNqDscrMode("number")}
                  onChange={(value) => {
                    setNqDscrMode("number");
                    setNqDscr(value ?? 1.2);
                  }}
                  min={0.01}
                  step={0.05}
                />
                <Button
                  className="ppe-dscr-no-ratio"
                  type={nqDscrMode === "noRatio" ? "primary" : "default"}
                  aria-pressed={nqDscrMode === "noRatio"}
                  onClick={() => setNqDscrMode("noRatio")}
                >
                  No Ratio
                </Button>
              </div>
              <div className="ppe-nonqm-field-hint">
                {nqDscrMode === "noRatio" ? "No DSCR value is required." : "Enter the qualifying DSCR value."}
              </div>
          </div>}
        </div>
        <div className="ppe-nonqm-field-group ppe-nonqm-housing-group">
          <div className="ppe-nonqm-group-title">Housing Event</div>
          <div className="ppe-nonqm-group-fields">
          <div className="ppe-nonqm-field">
            <div className="ppe-nonqm-label">Housing event type</div>
            <Select
              style={{ width: "100%", borderRadius: 6, height: 44 }}
              value={nqHousingEvent}
              onChange={(value) => {
                setNqHousingEvent(value);
                setNqHousingSeasoning(value ? DEFAULT_SEASONING_BUCKET : "");
              }}
            >
              <option value="">None</option>
              <option value="foreclosure">Foreclosure</option>
              <option value="shortSale">Short Sale</option>
              <option value="deedInLieu">Deed-in-Lieu</option>
              <option value="forbearance">Forbearance</option>
              <option value="modification">Modification</option>
            </Select>
          </div>
          {nqHousingEvent && (
            <div className="ppe-nonqm-field">
              <div className="ppe-nonqm-label">Housing event seasoning</div>
              <Select style={{ width: "100%", borderRadius: 6, height: 44 }} value={nqHousingSeasoning} onChange={setNqHousingSeasoning}>{seasoningOptions}</Select>
            </div>
          )}
          </div>
        </div>
        <div className="ppe-nonqm-field-group ppe-nonqm-bankruptcy-group">
          <div className="ppe-nonqm-group-title">Bankruptcy</div>
          <div className="ppe-nonqm-group-fields">
          <div className="ppe-nonqm-field">
            <div className="ppe-nonqm-label">Bankruptcy Type</div>
            <Select
              style={{ width: "100%", borderRadius: 6, height: 44 }}
              value={nqBkType}
              onChange={(value) => {
                setNqBkType(value);
                setNqBkOutcome(value ? DEFAULT_BANKRUPTCY_OUTCOME : "");
                setNqBkSeasoning(value ? DEFAULT_SEASONING_BUCKET : "");
              }}
            >
              <option value="">None</option>
              <option value="chapter7">Chapter 7</option>
              <option value="chapter11">Chapter 11</option>
              <option value="chapter13">Chapter 13</option>
            </Select>
          </div>
          {nqBkType && (
            <>
              <div className="ppe-nonqm-field">
                <div className="ppe-nonqm-label">Bankruptcy outcome</div>
                <Select style={{ width: "100%", borderRadius: 6, height: 44 }} value={nqBkOutcome} onChange={setNqBkOutcome}>
                  <option value="discharged">Discharged</option>
                  <option value="dismissed">Dismissed</option>
                  <option value="openPending">Open / Pending</option>
                </Select>
              </div>
              <div className="ppe-nonqm-field">
                <div className="ppe-nonqm-label">Bankruptcy seasoning</div>
                <Select style={{ width: "100%", borderRadius: 6, height: 44 }} value={nqBkSeasoning} onChange={setNqBkSeasoning}>{seasoningOptions}</Select>
              </div>
            </>
          )}
          </div>
        </div>
      </div>
      <div className="ppe-mortgage-lates-section">
        <div className="ppe-nonqm-group-title">Mortgage Lates</div>
        <div className="ppe-nonqm-label">Does the borrower have any mortgage late payments?</div>
        <Radio.Group
          className="ppe-mortgage-lates-choice"
          optionType="button"
          buttonStyle="solid"
          value={nqHasMortgageLates}
          onChange={(event) => setNqHasMortgageLates(event.target.value)}
        >
          <Radio.Button value={false}>No</Radio.Button>
          <Radio.Button value={true}>Yes</Radio.Button>
        </Radio.Group>
        {!nqHasMortgageLates && (
          <div className="ppe-mortgage-lates-hint">
            Select Yes to enter mortgage late details.
          </div>
        )}
        {nqHasMortgageLates && (
          <div className="ppe-lates-wrap">
            <div className="ppe-nonqm-label">Mortgage Lates</div>
            <div className="ppe-lates">
              <div className="ppe-lates-row">
                <div className="ppe-lates-rowlabel" />
                {["×30", "×60", "×90", "×120"].map((h) => (
                  <div className="ppe-lates-colhead" key={h}>{h}</div>
                ))}
              </div>
              <div className="ppe-lates-row">
                <div className="ppe-lates-rowlabel">Last 12 mo</div>
                {["l12_30", "l12_60", "l12_90", "l12_120"].map((k) => (
                  <InputNumber key={k} className="ppe-lates-input" controls={false} min={0} value={nqLates[k]} onChange={setLate(k)} />
                ))}
              </div>
              <div className="ppe-lates-row">
                <div className="ppe-lates-rowlabel">13-24 mo</div>
                {["l24_30", "l24_60", "l24_90", "l24_120"].map((k) => (
                  <InputNumber key={k} className="ppe-lates-input" controls={false} min={0} value={nqLates[k]} onChange={setLate(k)} />
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
      <div className="ppe-nonqm-divider" />
      <div className="ppe-nonqm-checks">
        <Checkbox checked={nqShortTermRental} onChange={(e) => setNqShortTermRental(e.target.checked)}>Short Term Rental</Checkbox>
        <Checkbox checked={nqVacant} onChange={(e) => setNqVacant(e.target.checked)}>Vacant / Unleased</Checkbox>
        <Checkbox checked={nqFirstTimeInvestor} onChange={(e) => setNqFirstTimeInvestor(e.target.checked)}>First Time Investor</Checkbox>
        <Checkbox checked={nqEntityVesting} onChange={(e) => setNqEntityVesting(e.target.checked)}>Entity Vesting</Checkbox>
        <Checkbox checked={nqRural} onChange={(e) => setNqRural(e.target.checked)}>Rural Property</Checkbox>
        <Checkbox checked={nqUnique} onChange={(e) => setNqUnique(e.target.checked)}>Unique Property</Checkbox>
      </div>
    </div>
  );

  return (
    <div className="search-fields">
      <div className="title-box">
        <div className="title-box-left" style={{display: "flex", alignItems: "center"}}>
          Search fields
          {from === "result" && <div className="cursor-pointer" style={{marginLeft: 8}} onClick={() => backToResult()}>{svg_collapse()}</div>}
        </div>
        <div className="title-box-right">
          <div
            className="title-box-right-reset cursor-pointer"
            onClick={reset_all}
          >
            <span>Reset all</span>
          </div>
          {!isLoading && 
          <div
            className="title-box-right-check cursor-pointer"
            onClick={check_rates}
          >
            <span>Check rates</span>
          </div>}
        </div>
      </div>
      <div className="search-fields-content">
        {field_Loan()}
        {field_Property()}
        {field_Borrower()}
        {field_Product()}
      </div>
      {loanProgram === "nonqm" && field_NonQM()}

      {/* no lender existing */}
      <NoLenderModal isOpen={isOpen_no_lender} onClose={closeModal_no_lender} addLenders={addLenders}/>
      {getEditCostsModal()}
      <AddLenderModal />
    </div>
  );
};

export default SearchFields;
